import { useNavigate, useParams, useLocation,  } from 'react-router-dom';
import React, { useState, useEffect,useCallback,useRef } from 'react';
import { createPortal } from 'react-dom';
import { doc as firestoreDoc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../services/firebase';
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { ReactComponent as ViticulturaIcon } from '../assets/icons/ViticulturaIcon.svg';
import { ReactComponent as EmbotelladoIcon } from '../assets/icons/EmbotelladoIcon.svg';
import { ReactComponent as VinificacionIcon } from '../assets/icons/VinificacionIcon.svg';
import { ReactComponent as SearchIcon } from '../assets/icons/SearchIcon.svg';
import LoadingSpinner from "../components/LoadingSpinner";
import { getAuth } from "firebase/auth";
import { ReactComponent as ClipIcon } from '../assets/icons/ClipIcon.svg';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import VinoCreadoModal from './VinoCreadoModal';
import FechaDateInput from './FechaDateInput';

// Todos los valores de volumen se guardan en hectolitros; este dropdown solo cambia cómo se muestran.
const UnidadVolumenToggle = ({ unidad, onChange }) => (
<div className="input-group" style={{ margin: 0, minWidth: '160px' }}>
<Listbox value={unidad} onChange={onChange}>
{({ open }) => (
<>
<ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} selected`}>
<span className="listbox-button-label">{unidad}</span>
<span className="custom-arrow-container">
<span className="custom-arrow"></span>
</span>
</ListboxButton>
<ListboxOptions className="custom-listbox-options">
{['Hectolitros', 'Litros'].map((opcion) => (
<ListboxOption key={opcion} value={opcion} className="custom-listbox-option">
{opcion}
</ListboxOption>
))}
</ListboxOptions>
</>
)}
</Listbox>
</div>
);

// El backend genera los IDs con prefijo fijo, así que el prefijo identifica la colección.
const COLECCION_POR_PREFIJO = [
  ['TRAZ', { nombre: 'trazabilidad', etiqueta: 'Trazabilidad' }],
  ['VIT', { nombre: 'viticultura', etiqueta: 'Viticultura' }],
  ['VIN', { nombre: 'vinificacion', etiqueta: 'Vinificacion' }],
  ['BOT', { nombre: 'embotellado', etiqueta: 'Embotellado' }],
];

// Lectura puntual de un documento (1 read). Devuelve null si no existe.
const fetchDocPuntual = async (coleccion, docId) => {
  const snap = await getDoc(firestoreDoc(db, coleccion, docId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
};

// Mismo patrón de hover que el Tooltip de Compras.js (medir la posición del
// trigger al entrar el mouse y flotar el contenido debajo, con un delay para
// no disparar en cada pasada). Genérico: sirve tanto para listar vinificaciones
// (consumo de quintales de un lote) como embotellados (consumo de hl de una
// partida) — 'usos' es [{id, ...}], 'formatPrefijo' arma el texto antes del ID.
const UsosTooltip = ({ usos, formatPrefijo, navigate, children, delay = 250 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const ref = useRef();
  const openTimeoutRef = useRef();
  const closeTimeoutRef = useRef();
  const handleMouseEnter = () => {
    const rect = ref.current.getBoundingClientRect();
    setCoords({
      top: rect.bottom + window.scrollY + 5,
      left: rect.left + window.scrollX + 10,
    });
    clearTimeout(closeTimeoutRef.current);
    openTimeoutRef.current = setTimeout(() => setIsHovered(true), delay);
  };
  // No cierra al toque: dejar un margen para que el mouse pueda cruzar el
  // hueco entre el trigger y el cartel sin que se cierre antes de llegar al
  // link (si en ese margen entra al cartel, handleTooltipEnter lo cancela).
  const handleMouseLeave = () => {
    clearTimeout(openTimeoutRef.current);
    closeTimeoutRef.current = setTimeout(() => setIsHovered(false), 200);
  };
  const handleTooltipEnter = () => {
    clearTimeout(closeTimeoutRef.current);
  };
  if (!usos || usos.length === 0) return children;
  // Ojo: NO envolver "children" en un <div> nuevo acá. Si se hiciera, ese <p>
  // pasaría a ser hijo único de ese div en vez de hijo directo de la columna,
  // y ".data-column.long .data-p:last-of-type" (padding-bottom: 100px) se
  // dispara para CUALQUIER <p> que sea el único de su tipo en su propio
  // padre — no sólo para el que de verdad es el último de la columna visual.
  // Por eso los handlers de hover se inyectan directo sobre el <p> original.
  return (
    <>
      {React.cloneElement(children, { ref, onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave })}
      {isHovered && createPortal(
        <div
          onMouseEnter={handleTooltipEnter}
          onMouseLeave={handleMouseLeave}
          style={{
            position: 'absolute',
            top: coords.top,
            left: coords.left,
            backgroundColor: '#f9fafd',
            padding: '12px 16px',
            borderRadius: '4px',
            whiteSpace: 'normal',
            zIndex: 1000,
            minWidth: '160px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: '4px',
          }}
        >
          {usos.map((u) => (
            <div key={u.id} style={{ fontSize: '13px', fontFamily: 'Effra', color: '#2c2c2c' }}>
              {formatPrefijo ? formatPrefijo(u) : ''}
              <span
                style={{ color: '#e65c5c', cursor: 'pointer' }}
                onClick={() => navigate(`/trazabilidad/${u.id}`)}
              >
                {u.id}
              </span>
            </div>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};

const EtiquetaTabContent = ({ doc, mostrar, refetchDoc }) => {
// Marca (ej. "Chirz", "Red Social") + Variedad (ej. "Asado Red", "Balcony
// White") son dos campos reales que arman el nombre público del vino. Antes
// no había dónde cargarlos y vino_nombre quedaba con el profesional
// responsable, que no es el nombre del vino.
const [marca, setMarca] = useState(doc.marca || '');
const [variedad, setVariedad] = useState(doc.variedad || '');
const [precios, setPrecios] = useState({});
const [etiquetaPrivada, setEtiquetaPrivada] = useState(doc.etiqueta_privada || '');
const [isLoading, setIsLoading] = useState(false);
const [cuposAsignados, setCuposAsignados] = useState({});
// Aviso post-creación (solo vinos 'Propio', ver handleCrearVino): reemplaza
// la navegación automática a /stock. null = no se muestra.
const [vinoCreado, setVinoCreado] = useState(null);
// Cápsula a usar para cada línea de embotellado, elegida acá (etapa de
// etiquetado) igual que precio y cupo. Vive en 'stock' (categoría Insumos)
// con la misma convención de nombre que botella/tapón ("Cápsula ...").
const [capsulas, setCapsulas] = useState({});
const [capsulasDisponibles, setCapsulasDisponibles] = useState([]);
// Etiqueta a usar para cada línea de embotellado — mismo patrón que la
// cápsula de arriba: vive en 'stock' (categoría Insumos) con la convención
// de nombre "Etiqueta ...".
const [etiquetas, setEtiquetas] = useState({});
const [etiquetasDisponibles, setEtiquetasDisponibles] = useState([]);
// Sin filtrar por stock disponible (a diferencia de *Disponibles arriba):
// una vez creado el vino, la cápsula/etiqueta usada puede haber quedado en
// 0 unidades y ya no aparecería en *Disponibles — esto es sólo para poder
// mostrar su nombre en modo lectura, no para elegir.
const [capsulasPorId, setCapsulasPorId] = useState({});
const [etiquetasPorId, setEtiquetasPorId] = useState({});
const [progress, setProgress] = useState(0);
const [descripcion, setDescripcion] = useState(doc.descripcion || '');
// Vino comprado (todas sus partidas vienen de 'stock', no de vinificación
// propia): no hay cadena de viticultura/vinificación de la que sacar el
// alcohol ni la fecha de cosecha, así que se cargan a mano acá. El alcohol
// es obligatorio (todo vino tiene), la cosecha es opcional (no siempre se
// sabe de un vino comprado a un tercero).
const [graduacionAlcoholica, setGraduacionAlcoholica] = useState(doc.graduacion_alcoholica ?? '');
const [fechaCosechaManual, setFechaCosechaManual] = useState(doc.fecha_cosecha ?? '');
// Vino ya creado, corrigiendo lo descriptivo (marca, variedad, descripción,
// imagen, alcohol, cosecha) -- ver CAMPOS_EDITABLES_VINO del backend. Precio,
// cápsula, etiqueta física y cupo NO se editan más allá de este punto: ya
// movieron stock/plata al crear el vino.
const [editandoVino, setEditandoVino] = useState(false);
const [guardandoEdicionVino, setGuardandoEdicionVino] = useState(false);
// Snapshot de los campos editables al entrar en modo edición, para poder
// descartar los cambios con "Cancelar" sin perder lo que había antes.
const snapshotEdicionVinoRef = useRef(null);
const isCreado = doc.etiqueta;
// Vino ya creado y NO se está corrigiendo ahora: mostrar de sólo lectura.
// Si isCreado && editandoVino, los campos descriptivos (marca, variedad,
// descripción, imagen, alcohol, cosecha) vuelven a ser editables -- precio,
// cápsula, etiqueta física y cupo (columna de al lado) nunca lo son, ver
// CAMPOS_EDITABLES_VINO del backend.
const soloLectura = isCreado && !editandoVino;
const navigate = useNavigate();
const [etiquetaFile, setEtiquetaFile] = useState(null);
const [etiquetaImageUrl, setEtiquetaImageUrl] = useState(doc.etiqueta_imagen_url || '');
const [uploadingImage, setUploadingImage] = useState(false);
const fileInputRef = useRef(null);
useEffect(() => {
const fetchCapsulas = async () => {
  try {
    const snapStock = await getDocs(collection(db, 'stock'));
    // Misma convención que botella/tapón en el embotellado: se distingue por
    // cómo se nombra el artículo al cargar la compra, no por un campo aparte.
    const empiezaConCapsula = (texto) => {
      const t = (texto || '').trim().toLowerCase();
      return t.startsWith('cápsula') || t.startsWith('capsula');
    };
    const empiezaConEtiqueta = (texto) => (texto || '').trim().toLowerCase().startsWith('etiqueta');
    const items = snapStock.docs
      .filter(d => !d.id.startsWith('DEMO-'))
      .map(d => ({ id: d.id, ...d.data() }));
    setCapsulasDisponibles(items.filter(i => i.categoria === 'Insumos' && empiezaConCapsula(i.nombre || i.id) && Number(i.cantidadUnidades) > 0));
    setEtiquetasDisponibles(items.filter(i => i.categoria === 'Insumos' && empiezaConEtiqueta(i.nombre || i.id) && Number(i.cantidadUnidades) > 0));
    const porId = {};
    items.forEach(i => { porId[i.id] = i; });
    setCapsulasPorId(porId);
    setEtiquetasPorId(porId);
  } catch (error) {
    console.error('Error al cargar cápsulas/etiquetas de stock:', error);
  }
};
fetchCapsulas();
}, []);
useEffect(() => {
if (isCreado && doc.embotellados_detalle) {
    const initialPrices = {};
    const initialCupos = {}; // 👈 Inicialización para cupos
    const initialCapsulas = {};
    const initialEtiquetas = {};
    doc.embotellados_detalle.forEach((p, index) => {
      if (p.precio_unitario) {
        initialPrices[index] = p.precio_unitario;
      }
      if (p.cupo_asignado) { // Asumiendo que esta propiedad existe en el detalle
        initialCupos[index] = p.cupo_asignado;
      }
      if (p.capsula_id) {
        initialCapsulas[index] = p.capsula_id;
      }
      if (p.etiqueta_id) {
        initialEtiquetas[index] = p.etiqueta_id;
      }
    });
    setPrecios(initialPrices);
    setCuposAsignados(initialCupos); // 👈 Establecer cupos iniciales
    setCapsulas(initialCapsulas);
    setEtiquetas(initialEtiquetas);
  }
if (isCreado && doc.etiqueta_privada) {
setEtiquetaPrivada(doc.etiqueta_privada);
}
if (isCreado && doc.descripcion) {
setDescripcion(doc.descripcion);
}
if (isCreado && doc.marca) {
setMarca(doc.marca);
}
if (isCreado && doc.variedad) {
setVariedad(doc.variedad);
}
// Vino todavía no creado pero con progreso guardado con "Guardar cambios"
// (ver guardar_borrador_etiqueta en el backend): se precarga para seguir
// completando en vez de arrancar de cero. Si ya se creó, no hace falta:
// arriba ya se cargó todo desde el vino/etiqueta real.
if (!isCreado && doc.etiqueta_borrador) {
const b = doc.etiqueta_borrador;
if (b.marca) setMarca(b.marca);
if (b.variedad) setVariedad(b.variedad);
if (b.descripcion) setDescripcion(b.descripcion);
if (b.etiqueta_privada) setEtiquetaPrivada(b.etiqueta_privada);
if (b.etiqueta_imagen_url) setEtiquetaImageUrl(b.etiqueta_imagen_url);
if (Array.isArray(b.lineas)) {
    const initialPrices = {};
    const initialCupos = {};
    const initialCapsulas = {};
    const initialEtiquetas = {};
    b.lineas.forEach((l, index) => {
      if (l.precio_unitario) initialPrices[index] = l.precio_unitario;
      if (l.cupo_asignado) initialCupos[index] = l.cupo_asignado;
      if (l.capsula_id) initialCapsulas[index] = l.capsula_id;
      if (l.etiqueta_id) initialEtiquetas[index] = l.etiqueta_id;
    });
    setPrecios(initialPrices);
    setCuposAsignados(initialCupos);
    setCapsulas(initialCapsulas);
    setEtiquetas(initialEtiquetas);
}
}
}, [doc, isCreado]);
const startProgressSimulation = () => {
setProgress(5);
const interval = setInterval(() => {
setProgress(prev => {
if (prev >= 95) {
clearInterval(interval);
return 95;
}
const increment = prev < 50 ? 5 : 1; 
return prev + increment;
});
}, 300);
return interval;
};
const handleCupoChange = (e, index) => { // 👈 Nueva función
  if (!isCreado && !isLoading) {
    const { value } = e.target;
    setCuposAsignados(prevCupos => ({
      ...prevCupos,
      [index]: value,
    }));
  }
};
const handleCapsulaChange = (value, index) => {
  if (!isCreado && !isLoading) {
    setCapsulas(prev => ({
      ...prev,
      [index]: value,
    }));
  }
};
const handleEtiquetaChange = (value, index) => {
  if (!isCreado && !isLoading) {
    setEtiquetas(prev => ({
      ...prev,
      [index]: value,
    }));
  }
};
// Cuánto se pidió de una misma cápsula sumando todas las líneas (igual que
// totalPedidoDeItem en el form de embotellado): una misma cápsula puede
// repetirse en más de una línea si dos tiradas comparten envase.
const totalPedidoDeCapsula = (capsulaId) => (doc.embotellados_detalle || []).reduce((total, p, index) => {
  if (capsulas[index] !== capsulaId) return total;
  return total + (Number(p.cantidad_botellas) || 0);
}, 0);
const hayCapsulaExcedida = (doc.embotellados_detalle || []).some((p, index) => {
  const capsulaId = capsulas[index];
  if (!capsulaId) return false;
  const capsulaElegida = capsulasDisponibles.find(c => c.id === capsulaId);
  if (!capsulaElegida) return false;
  return totalPedidoDeCapsula(capsulaId) > Number(capsulaElegida.cantidadUnidades || 0);
});
// Etiqueta: mismos dos chequeos que la cápsula de arriba.
const totalPedidoDeEtiqueta = (etiquetaId) => (doc.embotellados_detalle || []).reduce((total, p, index) => {
  if (etiquetas[index] !== etiquetaId) return total;
  return total + (Number(p.cantidad_botellas) || 0);
}, 0);
const hayEtiquetaExcedida = (doc.embotellados_detalle || []).some((p, index) => {
  const etiquetaId = etiquetas[index];
  if (!etiquetaId) return false;
  const etiquetaElegida = etiquetasDisponibles.find(e => e.id === etiquetaId);
  if (!etiquetaElegida) return false;
  return totalPedidoDeEtiqueta(etiquetaId) > Number(etiquetaElegida.cantidadUnidades || 0);
});
// Todo lo que hace falta para publicar el vino en la tienda. Mientras falte
// algo de esto, el botón guarda el progreso ("Guardar cambios") en vez de
// publicar — así se puede cargar de a poco sin que el vino salga a la venta
// con huecos (esto salió de un caso real: un vino quedó en la tienda sin
// imagen ni descripción). El cupo a asignar queda afuera a propósito: es
// opcional, no todos los vinos van al circuito de socios.
const esMarcaBlancaCheck = doc.tipo_uso === 'Marca Blanca';
const esCremaDeEstrellasCheck = doc.embotellado?.tipo_vino === 'Crema de Estrellas';
const nombreCompleto = esCremaDeEstrellasCheck || marca.trim().length > 0;
const textoCompleto = esMarcaBlancaCheck ? etiquetaPrivada.trim().length > 0 : descripcion.trim().length > 0;
const imagenCompleta = !!(etiquetaImageUrl || etiquetaFile);
const lineasCompletas = (doc.embotellados_detalle || []).length > 0 &&
  (doc.embotellados_detalle || []).every((p, index) => {
    const precioOk = precios[index] !== undefined && precios[index] !== '' && parseFloat(precios[index]) > 0;
    // La cápsula es opcional: hay vinos que no la usan. Si se elige una, sí
    // se valida y descuenta de stock (ver hayCapsulaExcedida más arriba);
    // si no se elige ninguna, la línea queda completa igual.
    return precioOk && !!etiquetas[index];
  });
// Repetido acá (además de más abajo, cerca de su uso original) porque hace
// falta antes en el archivo para el gating de 'listoParaCrear'. Un vino
// comprado no tiene de dónde sacar el alcohol automáticamente (no pasó por
// vinificación), así que se pide a mano; la cosecha, en cambio, es opcional.
const esVinoCompradoParaAlcohol = (doc.partidas_usadas || []).length > 0 &&
  (doc.partidas_usadas || []).every(partida => partida.origen === 'stock');
const alcoholCompleto = !esVinoCompradoParaAlcohol ||
  (graduacionAlcoholica !== '' && !isNaN(parseFloat(graduacionAlcoholica)) && parseFloat(graduacionAlcoholica) > 0);
const listoParaCrear = nombreCompleto && textoCompleto && imagenCompleta && lineasCompletas &&
  alcoholCompleto && !hayCapsulaExcedida && !hayEtiquetaExcedida;
const handleGuardarCambios = async () => {
let finalEtiquetaImageUrl = etiquetaImageUrl;
try {
const auth = getAuth();
const user = auth.currentUser;
if (!user) {
alert("Usuario no autenticado");
return;
}
setIsLoading(true);
const idToken = await user.getIdToken();
if (etiquetaFile) {
setUploadingImage(true);
finalEtiquetaImageUrl = await uploadImageToFirebase(etiquetaFile);
setUploadingImage(false);
}
const esCremaDeEstrellas = doc.embotellado?.tipo_vino === 'Crema de Estrellas';
const borrador = {
marca: esCremaDeEstrellas ? 'Imum Coeli' : marca.trim(),
  variedad: esCremaDeEstrellas ? null : (variedad.trim() || null),
descripcion: doc.tipo_uso !== 'Marca Blanca' ? descripcion : null,
etiqueta_privada: doc.tipo_uso === 'Marca Blanca' ? etiquetaPrivada : null,
etiqueta_imagen_url: finalEtiquetaImageUrl,
lineas: (doc.embotellados_detalle || []).map((p, index) => ({
    precio_unitario: precios[index] ? parseFloat(precios[index]) : null,
    cupo_asignado: cuposAsignados[index] ? parseInt(cuposAsignados[index]) : null,
    etiqueta_id: etiquetas[index] || null,
    capsula_id: capsulas[index] || null,
})),
};
const response = await fetch(`https://backend-288175208699.europe-west1.run.app/api/trazabilidad/${doc.id}/borrador-etiqueta`, {
method: 'PUT',
headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${idToken}`,
},
body: JSON.stringify(borrador),
});
if (!response.ok) {
const errorData = await response.json();
throw new Error(errorData.error || 'No se pudo guardar el progreso.');
}
setEtiquetaImageUrl(finalEtiquetaImageUrl);
setEtiquetaFile(null);
if (refetchDoc) {
await refetchDoc();
}
} catch (error) {
console.error("Error al guardar el borrador:", error);
alert(`Error: ${error.message || 'Error de conexión.'}`);
} finally {
setIsLoading(false);
setUploadingImage(false);
}
};
const handleCrearVino = async () => {
  const esMarcaBlanca = doc.tipo_uso === 'Marca Blanca';
  if (esMarcaBlanca && !etiquetaPrivada.trim()) {
    alert("Por favor, ingrese el texto de la etiqueta privada.");
    return;
  }

  // Se eliminó la validación vieja acá. Ahora solo exigimos la marca.
  if (doc.embotellado?.tipo_vino !== 'Crema de Estrellas' && !marca.trim()) {
    alert("Por favor, completá la marca del vino.");
    return;
  }

  if (esVinoCompradoParaAlcohol && (graduacionAlcoholica === '' || isNaN(parseFloat(graduacionAlcoholica)) || parseFloat(graduacionAlcoholica) <= 0)) {
    alert("Por favor, completá el alcohol (%) del vino comprado.");
    return;
  }

  let progressInterval = null;
  let finalEtiquetaImageUrl = etiquetaImageUrl;

  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      alert("Usuario no autenticado");
      return;
    }

    setIsLoading(true);
    progressInterval = startProgressSimulation();
    const idToken = await user.getIdToken();

    if (etiquetaFile) {
      setUploadingImage(true);
      finalEtiquetaImageUrl = await uploadImageToFirebase(etiquetaFile);
      setUploadingImage(false);
    }

    const totalDiasGuarda = doc.partidas_usadas.reduce((sum, partida) => sum + (partida.detalles?.dias_guarda || 0), 0);
    const totalCrianza = totalDiasGuarda + (doc.embotellado?.dias_estiba || 0);
    const nivelAzucar = partidaMaxHL.viticultura?.nivel_azucar;
    const clasificacionAzucar = nivelAzucar ? getClasificacionAzucar(nivelAzucar) : null;

    const esCremaDeEstrellas = doc.embotellado?.tipo_vino === 'Crema de Estrellas';
    const nombreVinoGuardar = esCremaDeEstrellas
      ? 'Imum Coeli'
      : [marca.trim(), variedad.trim()].filter(Boolean).join(' ');

    const vinoData = {
      trazabilidad_id: doc.id,
      embotellado: doc.embotellado.id,
      vino_nombre: nombreVinoGuardar,
      marca: esCremaDeEstrellas ? 'Imum Coeli' : marca.trim(),
      variedad: esCremaDeEstrellas ? null : (variedad.trim() || null),
      profesional_responsable: doc.embotellado?.profesional_responsable,
      tipo_vino: doc.embotellado?.tipo_vino,
      clasificacion_azucar: clasificacionAzucar,
      cepas: [...new Set(
        doc.partidas_usadas
          .map(p => p.viticultura?.variedad_uva || p.cepa)
          .filter(Boolean)
      )],
      anada: (() => {
        const mayor = doc.partidas_usadas.reduce(
          (max, partida) => (partida.cantidad_hl || 0) > (max?.cantidad_hl || 0) ? partida : max, null);
        return mayor?.viticultura?.fecha_cosecha?.slice(0, 4) || doc.embotellado?.anada || null;
      })(),
      tiempo_crianza: totalCrianza,
      clasificacion: totalCrianza >= 540 ? "Gran Reserva" : totalCrianza >= 180 ? "Reserva" : "Joven",
      graduacion_alcoholica: esVinoCompradoParaAlcohol ? parseFloat(graduacionAlcoholica) : null,
      fecha_cosecha: esVinoCompradoParaAlcohol ? (fechaCosechaManual || null) : null,
      etiqueta_privada: doc.tipo_uso === 'Marca Blanca' ? etiquetaPrivada : null,
      etiqueta_imagen_url:  finalEtiquetaImageUrl,
      descripcion: doc.tipo_uso !== 'Marca Blanca' ? descripcion : null,
      embotellados_detalle: doc.embotellados_detalle.map((p, index) => ({
        forma_botella: p.forma_botella,
        medida_botella: p.medida_botella,
        cantidad_botellas: p.cantidad_botellas,
        tipo_corcho: p.tipo_corcho,
        etiqueta_id: etiquetas[index] || null,
        capsula_id: capsulas[index] || null,
        precio_unitario: precios[index] ? parseFloat(precios[index]) : null,
        cupo_asignado: cuposAsignados[index] ? parseInt(cuposAsignados[index]) : null
      })),
    };

    const response = await fetch('https://backend-288175208699.europe-west1.run.app/crear-vinos', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify(vinoData),
    });

    if (response.ok) {
      const result = await response.json();
      console.log("Vino(s) creado(s) exitosamente:", result.ids);
      clearInterval(progressInterval);
      setProgress(100);

      if (esMarcaBlanca) {
        if (refetchDoc) {
          await refetchDoc();
        }
      } else {
        const totalBotellas = doc.embotellados_detalle.reduce(
          (total, p) => total + (Number(p.cantidad_botellas) || 0), 0
        );
        setVinoCreado({
          vinoNombre: nombreVinoGuardar,
          marca: esCremaDeEstrellas ? 'Imum Coeli' : marca.trim(),
          variedad: esCremaDeEstrellas ? null : (variedad.trim() || null),
          cepas: vinoData.cepas,
          anada: vinoData.anada,
          imagenUrl: finalEtiquetaImageUrl,
          totalBotellas,
          cuposAsignados: result.cupos_asignados || [],
          clientesConCupo: result.clientes_con_cupo || 0,
        });
      }
    } else {
      const errorData = await response.json();
      console.error("Error al crear el vino:", errorData.error);
      throw new Error(errorData.error || "Error desconocido al crear el vino.");
    }
  } catch (error) {
    console.error("Error en la solicitud:", error);
    alert(`Error: ${error.message || 'Error de conexión.'}`);
  } finally {
    if (progressInterval) clearInterval(progressInterval);
    setIsLoading(false);
    setUploadingImage(false);
    setTimeout(() => setProgress(0), 500);
  }
};
// Vino ya creado: corrige sólo lo descriptivo en 'vinos' (y su copia en la
// propia trazabilidad) -- ver CAMPOS_EDITABLES_VINO del backend. Precio,
// cápsula, etiqueta física, cupo y cantidad de botellas no se tocan acá.
const handleEntrarEdicionVino = () => {
snapshotEdicionVinoRef.current = {
marca, variedad, descripcion, etiquetaPrivada, etiquetaImageUrl,
graduacionAlcoholica, fechaCosechaManual,
};
setEditandoVino(true);
};
const handleCancelarEdicionVino = () => {
const snap = snapshotEdicionVinoRef.current;
if (snap) {
setMarca(snap.marca);
setVariedad(snap.variedad);
setDescripcion(snap.descripcion);
setEtiquetaPrivada(snap.etiquetaPrivada);
setEtiquetaImageUrl(snap.etiquetaImageUrl);
setGraduacionAlcoholica(snap.graduacionAlcoholica);
setFechaCosechaManual(snap.fechaCosechaManual);
}
setEtiquetaFile(null);
setEditandoVino(false);
};
const handleGuardarEdicionVino = async () => {
const esCremaDeEstrellas = doc.embotellado?.tipo_vino === 'Crema de Estrellas';
if (!esCremaDeEstrellas && !marca.trim()) {
  alert("Por favor, completá la marca del vino.");
  return;
}
if (esVinoCompradoParaAlcohol && (graduacionAlcoholica === '' || isNaN(parseFloat(graduacionAlcoholica)) || parseFloat(graduacionAlcoholica) <= 0)) {
alert("Por favor, completá el alcohol (%) del vino comprado.");
return;
}
setGuardandoEdicionVino(true);
setIsLoading(true); // Mismo flag que usan los inputs para deshabilitarse mientras se guarda.
let progressInterval = null;
try {
progressInterval = startProgressSimulation();
const auth = getAuth();
const user = auth.currentUser;
if (!user) {
alert("Usuario no autenticado");
return;
}
const idToken = await user.getIdToken();
let finalEtiquetaImageUrl = etiquetaImageUrl;
if (etiquetaFile) {
finalEtiquetaImageUrl = await uploadImageToFirebase(etiquetaFile);
setEtiquetaImageUrl(finalEtiquetaImageUrl);
setEtiquetaFile(null);
}
const payload = {
  marca: esCremaDeEstrellas ? 'Imum Coeli' : marca.trim(),
  variedad: esCremaDeEstrellas ? null : (variedad.trim() || null),
  etiqueta_privada: doc.tipo_uso === 'Marca Blanca' ? etiquetaPrivada : null,
  descripcion: doc.tipo_uso !== 'Marca Blanca' ? descripcion : null,
  etiqueta_imagen_url: finalEtiquetaImageUrl,
  graduacion_alcoholica: esVinoCompradoParaAlcohol ? parseFloat(graduacionAlcoholica) : null,
  fecha_cosecha: esVinoCompradoParaAlcohol ? (fechaCosechaManual || null) : null,
};
const response = await fetch(`https://backend-288175208699.europe-west1.run.app/api/trazabilidad/${doc.id}/editar-vino`, {
method: 'PUT',
headers: {
'Content-Type': 'application/json',
'Authorization': `Bearer ${idToken}`,
},
body: JSON.stringify(payload),
});
if (!response.ok) {
const errorData = await response.json();
throw new Error(errorData.error || "Error desconocido al editar el vino.");
}
if (progressInterval) clearInterval(progressInterval);
setProgress(100);
if (refetchDoc) {
await refetchDoc();
}
setEditandoVino(false);
} catch (error) {
console.error("Error al editar el vino:", error);
alert(`Error: ${error.message || 'Error de conexión.'}`);
} finally {
if (progressInterval) clearInterval(progressInterval);
setGuardandoEdicionVino(false);
setIsLoading(false);
setTimeout(() => setProgress(0), 500);
}
};
if (!doc || !doc.partidas_usadas || doc.partidas_usadas.length === 0) {
return <p>No hay datos disponibles para generar la etiqueta.</p>;
}
const partidaMaxHL = doc.partidas_usadas.reduce((max, partida) => {
return (partida.cantidad_hl || 0) > (max?.cantidad_hl || 0) ? partida : max;
}, null);
const totalDiasGuarda = doc.partidas_usadas.reduce((sum, partida) => {
return sum + (partida.detalles?.dias_guarda || 0);
}, 0);
const totalCrianza = totalDiasGuarda + (doc.embotellado?.dias_estiba || 0);
// Vino comprado no tiene guarda propia (no hay vinificación/viticultura
// detrás de dónde sacar 'dias_guarda'): mostrar "Tiempo total de crianza"
// ahí sería mostrar sólo la estiba y hacerlo pasar por la crianza completa.
const esVinoComprado = doc.partidas_usadas.length > 0 &&
doc.partidas_usadas.every(partida => partida.origen === 'stock');
// Reusados en la columna de resumen (izquierda) y, si ya se creó el vino, en
// el resumen de solo lectura que reemplaza el formulario (derecha).
const cepasUnicas = [...new Set(
doc.partidas_usadas.map(partida => partida.viticultura?.variedad_uva || partida.cepa).filter(Boolean)
)];
const anadaValor = partidaMaxHL.viticultura?.fecha_cosecha?.slice(0, 4) || doc.embotellado?.anada || null;
const handlePrecioChange = (e, index) => {
if (!isCreado && !isLoading) {
const { value } = e.target;
setPrecios(prevPrecios => ({
...prevPrecios,
[index]: value,
}));
}
};
const handleEtiquetaPrivadaChange = (e) => {
if (!soloLectura && !isLoading) {
setEtiquetaPrivada(e.target.value);
}
};
const handleDescripcionChange = (e) => {
if (!soloLectura && !isLoading) {
setDescripcion(e.target.value);
}
};
const uploadImageToFirebase = (file) => {
return new Promise((resolve, reject) => {
if (!file) {
return resolve(null);
}
const storage = getStorage();
const storageRef = ref(storage, `etiquetas_vino/${doc.id}/${file.name}`);
const uploadTask = uploadBytesResumable(storageRef, file);
uploadTask.on(
'state_changed',
(snapshot) => {
const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
console.log('Upload is ' + progress + '% done');
},
(error) => {
console.error("Error al subir la imagen:", error);
setUploadingImage(false);
reject(error);
},
() => {
getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
setEtiquetaImageUrl(downloadURL);
setUploadingImage(false);
resolve(downloadURL);
});
}
);
});
};
const handleFileChange = (e) => {
if (e.target.files[0]) {
setEtiquetaFile(e.target.files[0]);
}
};
const getClasificacionAzucar = (nivelAzucar) => {
const azucar = parseFloat(nivelAzucar);
if (isNaN(azucar)) return null;
if (azucar < 3) return 'Brut Nature';
if (azucar <= 6) return 'Extra Brut';
if (azucar <= 12) return 'Brut';
if (azucar <= 17) return 'Extra Seco';
if (azucar <= 32) return 'Seco';
if (azucar <= 50) return 'Demi-Sec';
if (azucar > 50) return 'Doux (o Dulce)';
return null;
};
const handleViewImage = () => {
if (isCreado && etiquetaImageUrl) {
window.open(etiquetaImageUrl, '_blank');
}
};
const getButtonText = () => {
if (isCreado) {
return 'Creado';
}
if (isLoading) {
return listoParaCrear ? 'Creando...' : 'Guardando...';
}
return listoParaCrear ? 'Crear vino' : 'Guardar cambios';
};
return (
<>
<div className="doc-detail embotellado-detail">
<div className="columns-container" style={{ display: 'flex', gap: '40px', paddingTop: '10px' }}>
<div className="data-column">
<label className="data-label">Nombre del creador</label>
<p className="data-p" style={{ fontWeight: '700' }}>
{mostrar(doc.embotellado.profesional_responsable)}
</p>
<label className="data-label">Tipo de vino</label>
<p className="data-p" style={{ textTransform: 'capitalize' }}>
{mostrar(doc.embotellado?.tipo_vino)}
</p>
<label className="data-label">Cepas utilizadas</label>
{/* Una línea por VARIEDAD única, no por partida: dos partidas de Malbec
    de proveedores distintos (mismo blend) mostraban "Malbec" repetido. */}
{cepasUnicas.map((cepa, index) => (
<p key={index} className="data-p" style={{ margin: '0' }}>
{mostrar(cepa)}
</p>
))}
<label className="data-label">Añada</label>
<p className="data-p">{mostrar(anadaValor)}</p>
{!esVinoComprado && (
<>
<label className="data-label">Tiempo total de crianza</label>
<p className="data-p">{Number(totalCrianza).toLocaleString("es-AR")}d</p>
</>
)}
{esVinoComprado && (
<>
{/* Vino comprado: no hay vinificación/viticultura de la que sacar
    alcohol ni cosecha, se cargan a mano. Alcohol es obligatorio
    (ver 'alcoholCompleto' en listoParaCrear); cosecha es opcional
    y, si queda vacía, la tienda no muestra esa línea. */}
<label className="data-label" style={soloLectura ? undefined : {color: '#e65c5c'}}>Alcohol (%)</label>
{soloLectura ? (
<p className="data-p">{graduacionAlcoholica !== '' ? `${graduacionAlcoholica}%` : '-'}</p>
) : (
<div className='input-group number' style={{width: '70%'}}>
<input
type="number"
step="0.01"
min="0"
placeholder="Ej: 13.5"
value={graduacionAlcoholica}
disabled={isLoading}
onChange={(e) => setGraduacionAlcoholica(e.target.value)}
className='input'
/>
<span className='unit'>%</span>
</div>
)}
{(!soloLectura || fechaCosechaManual) && (
<>
<label className="data-label">Fecha de cosecha</label>
{soloLectura ? (
<p className="data-p">{fechaCosechaManual}</p>
) : (
<div className='input-group' style={{width: '70%'}}>
<FechaDateInput
value={fechaCosechaManual}
disabled={isLoading}
onChange={setFechaCosechaManual}
className='input'
/>
</div>
)}
</>
)}
</>
)}
</div>
<div className="data-column long">
{doc.embotellados_detalle?.length > 0 ? (
doc.embotellados_detalle.map((p, index) => (
<React.Fragment key={index}>
{index > 0 && <hr style={{ border: '0', borderTop: '1px solid #ccc', margin: '10px 0' }} />}
<label className="data-label">Tipo de botella</label>
<p className="data-p">{p.forma_botella}</p>
<label className="data-label">Medida de la botella</label>
<p className="data-p">{p.medida_botella}</p>
<label className="data-label">Cantidad de botellas</label>
<p className="data-p">{Number(p.cantidad_botellas).toLocaleString("es-AR")}</p>
<label className="data-label">Tapón</label>
{/* El CSS le agrega padding-bottom a ".data-p:last-of-type" pensando que ese
    p es lo último de la columna. Acá no lo es (siguen Cápsula, Cupo y Precio,
    que son divs, no p) — se anula para que no infle este renglón. */}
<p className="data-p" style={{paddingBottom: 0}}>{p.tipo_corcho}</p>
{isCreado ? (
  <>
    <label className="data-label" style={{ marginTop: '20px' }}>Etiqueta utilizada</label>
    <p className="data-p">{mostrar(etiquetasPorId[etiquetas[index]]?.nombre || etiquetas[index])}</p>
    {capsulas[index] && (
      <>
        <label className="data-label">Cápsula utilizada</label>
        <p className="data-p">{mostrar(capsulasPorId[capsulas[index]]?.nombre || capsulas[index])}</p>
      </>
    )}
    {cuposAsignados[index] && (
      <>
        <label className="data-label">Cupo asignado</label>
        <p className="data-p">{cuposAsignados[index]} u.</p>
      </>
    )}
    <label className="data-label" style={{paddingBottom: 0}}>Precio unitario</label>
    <p className="data-p" style={{paddingBottom: 0}}>
      {precios[index] ? `$ ${Number(precios[index]).toLocaleString('es-AR')}` : '-'}
    </p>
  </>
) : (
  <>
    {(() => {
      const etiquetaId = etiquetas[index] || '';
      const etiquetaElegida = etiquetasDisponibles.find(e => e.id === etiquetaId);
      const sePasaDeStockEtiqueta = etiquetaElegida && totalPedidoDeEtiqueta(etiquetaId) > Number(etiquetaElegida.cantidadUnidades || 0);
      return (
        <>
          <label className="data-label" style={{color: '#e65c5c', marginTop: '20px'}}>
              Etiqueta a utilizar
          </label>
          <div className="input-group" style={{width: '50%'}}>
            <Listbox
              value={etiquetaId}
              onChange={(value) => handleEtiquetaChange(value, index)}
              disabled={isLoading}
            >
              {({ open }) => (
                <>
                  <ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${etiquetaId ? 'selected' : 'placeholder'}`}>
                    <span className="listbox-button-label">
                      {etiquetaElegida ? (etiquetaElegida.nombre || etiquetaElegida.id) : (etiquetaId || 'Seleccionar etiqueta')}
                    </span>
                    <span className="custom-arrow-container">
                      <span className="custom-arrow"></span>
                    </span>
                  </ListboxButton>
                  <ListboxOptions className="custom-listbox-options">
                    {etiquetasDisponibles.map((e) => (
                      <ListboxOption key={e.id} value={e.id} className="custom-listbox-option">
                        {e.nombre || e.id}
                        <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
                          {Number(e.cantidadUnidades || 0).toLocaleString('es-AR')} u.
                        </span>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </>
              )}
            </Listbox>
          </div>
          {etiquetaElegida && (
            <span className="data-label" style={{margin:'0px 0 10px', flexDirection:'row', display:'flex', alignItems:'center', gap: '5px'}}>
              Disponible: {Number(etiquetaElegida.cantidadUnidades || 0).toLocaleString('es-AR')} u.
            </span>
          )}
          {sePasaDeStockEtiqueta && (
            <span style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra', margin: '0px 0 10px' }}>
              Superaste el stock disponible de etiquetas.
            </span>
          )}
        </>
      );
    })()}
    {(() => {
      const capsulaId = capsulas[index] || '';
      const capsulaElegida = capsulasDisponibles.find(c => c.id === capsulaId);
      const sePasaDeStock = capsulaElegida && totalPedidoDeCapsula(capsulaId) > Number(capsulaElegida.cantidadUnidades || 0);
      return (
        <>
          <label className="data-label" style={{color: '#e65c5c'}}>
              Cápsula a utilizar
          </label>
          <div className="input-group" style={{width: '50%'}}>
            <Listbox
              value={capsulaId}
              onChange={(value) => handleCapsulaChange(value, index)}
              disabled={isLoading}
            >
              {({ open }) => (
                <>
                  <ListboxButton className={`custom-listbox-button ${open ? 'open' : ''} ${capsulaId ? 'selected' : 'placeholder'}`}>
                    <span className="listbox-button-label">
                      {capsulaElegida ? (capsulaElegida.nombre || capsulaElegida.id) : (capsulaId || 'Este vino no usa cápsula')}
                    </span>
                    <span className="custom-arrow-container">
                      <span className="custom-arrow"></span>
                    </span>
                  </ListboxButton>
                  <ListboxOptions className="custom-listbox-options">
                    <ListboxOption key="__sin_capsula" value="" className="custom-listbox-option">
                      Sin cápsula
                    </ListboxOption>
                    {capsulasDisponibles.map((c) => (
                      <ListboxOption key={c.id} value={c.id} className="custom-listbox-option">
                        {c.nombre || c.id}
                        <span style={{ color: '#999', fontSize: '12px', marginLeft: '8px' }}>
                          {Number(c.cantidadUnidades || 0).toLocaleString('es-AR')} u.
                        </span>
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </>
              )}
            </Listbox>
          </div>
          {capsulaElegida && (
            <span className="data-label" style={{margin:'0px 0 10px', flexDirection:'row', display:'flex', alignItems:'center', gap: '5px'}}>
              Disponible: {Number(capsulaElegida.cantidadUnidades || 0).toLocaleString('es-AR')} u.
            </span>
          )}
          {sePasaDeStock && (
            <span style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra', margin: '0px 0 10px' }}>
              Superaste el stock disponible de cápsulas.
            </span>
          )}
        </>
      );
    })()}
    <label className="data-label" style={{color: '#e65c5c'}}>
        Cupo a asignar
    </label>
    <div className='input-group number' style={{width: '50%'}}>
        <input
            type="number"
            placeholder="Ej: 500"
            value={cuposAsignados[index] || ''}
            disabled={isLoading}
            onChange={(e) => handleCupoChange(e, index)}
            min="0"
            max={p.cantidad_botellas} // Opcional: limitar al total de botellas
            className='input'
        />
        <span className='unit'>u</span>
    </div>
    <label className="data-label" style={{color: '#e65c5c'}}>
    Establecer precio unitario
    </label>
    <div className='input-group number' style={{width: '50%', marginBottom: '20px'}}>
    <input
    type="number"
    placeholder="Ej: 15,50"
    value={precios[index] || ''}
    disabled={isLoading}
    onChange={(e) => handlePrecioChange(e, index)}
    min="0"
    className='input'
    /><span className='unit'>$</span>
    </div>
  </>
)}
</React.Fragment>
))
) : (
<p className="data-p">No hay partidas registradas</p>
)}
</div>
<div className="column long" style={{overflow:'auto',paddingBottom:'100px'}}>
{soloLectura ? (
// Vino ya creado y no se está editando: misma estructura que
// VinoCreadoModal (foto + datos en texto plano). Lo que sí se puede
// corregir (ver CAMPOS_EDITABLES_VINO del backend) es a través del botón
// "Editar" (soloLectura pasa a false y se muestra el formulario de abajo).
<div style={{ textAlign: 'center' }}>
{etiquetaImageUrl && (
<div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
<img
src={etiquetaImageUrl}
alt={marca}
onClick={handleViewImage}
style={{ width: '100%', maxHeight: '30vh', objectFit: 'contain', marginBottom: '0', cursor: 'pointer' }}
/>
<span style={{
position: 'absolute',
top: '10px',
right: '10px',
backgroundColor: '#39b54a', // mismo verde que .button.finalizado (el que usaba el botón "Creado")
color: '#fff',
padding: '4px 12px',
borderRadius: '999px',
fontSize: '12px',
fontFamily: 'Effra',
letterSpacing: '0.02em',
}}>
En tienda
</span>
</div>
)}
<p style={{ fontWeight: 700, fontSize: '24px', margin: '0 0 4px' }}>
{marca}
</p>
{!esCremaDeEstrellasCheck && variedad && (
<p className="data-p" style={{ margin: '0 0 4px' }}>{variedad}</p>
)}
{cepasUnicas.length > 0 && (
<p className="data-p" style={{ margin: '0 0 4px' }}>{cepasUnicas.join(', ')}</p>
)}
{anadaValor && (
<p className="data-p" style={{ margin: '0 0 4px' }}>{anadaValor}</p>
)}
</div>
) : (
<>
<label className="data-label" style={{ marginTop: '10px' }}>Formato de botella</label>
<div className="input-group">
<input
type="text"
placeholder="Nombre de marca"
value={marca}
disabled={isLoading}
onChange={(e) => setMarca(e.target.value)}
className="input"
/>
</div>
<div className="input-group">
<input
type="text"
placeholder="Variedad"
value={variedad}
disabled={isLoading}
onChange={(e) => setVariedad(e.target.value)}
className="input"
/>
</div>
{doc.tipo_uso === 'Marca Blanca' ? (
<>
<label className="data-label">Etiqueta Privada</label>
<div className="post-mortem-textarea-group">
<textarea
className="post-mortem-textarea"
rows="8"
maxLength={825}
placeholder="Escribe el texto de la etiqueta privada aquí..."
value={etiquetaPrivada}
disabled={isLoading}
onChange={handleEtiquetaPrivadaChange}
/>
<div style={{
textAlign: 'right',
fontSize: '12px',
color: '#888',
marginRight: '5px',
fontFamily: 'Effra'
}}>
{etiquetaPrivada.length} / 825
</div>
</div>
</>
) : (
<>
{}
<label className="data-label">Descripción</label>
<div className="post-mortem-textarea-group">
<textarea
className="post-mortem-textarea"
rows="4"
placeholder="Escribe una descripción corta para este vino..."
value={descripcion}
disabled={isLoading}
onChange={handleDescripcionChange}
/>
</div>
{}
{}
{doc.embotellado?.tipo_vino !== 'Crema de Estrellas' && (
<>
{totalCrianza >= 180 && totalCrianza < 540 ? (
<p className="data-p" style={{ textAlign: 'center' , margin: '0'}}>Reserva</p>
) : totalCrianza >= 540 ? (
<p className="data-p" style={{ textAlign: 'center' , margin: '0'}}>Gran Reserva</p>
) : null}
</>
)}
</>
)}
{}
<label className="data-label" style={{ marginTop: '5px' }}>Imagen de la Etiqueta</label>
<input
type="file"
accept="image/*"
ref={fileInputRef}
onChange={handleFileChange}
disabled={isLoading || uploadingImage}
style={{ display: 'none' }}
/>
<button
type="button"
onClick={() => fileInputRef.current.click()}
disabled={isLoading || uploadingImage}
className="pdf-button"
>
<ClipIcon style={{ height: '15px', marginRight: '5px' }} />
{etiquetaFile
? etiquetaFile.name
: (etiquetaImageUrl ? 'Imagen Cargada' : 'Seleccionar Archivo')
}
</button>
</>
)}
<div className="button-container" style={{ marginTop: '20px', gap: '10px' }}>
<hr style={{ border: '0', borderTop: '1px solid #eaeef5', margin: '0px 0 0px' }} />
{isCreado && !editandoVino ? (
<button
type="button"
className="button"
onClick={handleEntrarEdicionVino}
>
<span className="button-text">Editar</span>
</button>
) : isCreado && editandoVino ? (
<div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
<button
type="button"
className="button"
onClick={handleCancelarEdicionVino}
disabled={guardandoEdicionVino}
style={{ flex: 1 }}
>
<span className="button-text">Cancelar</span>
</button>
<button
type="button"
className={`button ${guardandoEdicionVino ? 'cargando' : ''} button-with-progress`}
onClick={handleGuardarEdicionVino}
disabled={guardandoEdicionVino}
style={{ flex: 1 }}
>
<span className="button-text">
{guardandoEdicionVino ? 'Guardando...' : 'Guardar cambios'}
</span>
{guardandoEdicionVino && (
<span className="progress-bar" style={{ width: `${progress}%` }}></span>
)}
</button>
</div>
) : (
<button
className={`button ${isLoading ? 'cargando' : ''} button-with-progress`}
onClick={listoParaCrear ? handleCrearVino : handleGuardarCambios}
disabled={isLoading}
>
<span className="button-text">
{getButtonText()}
</span>
{isLoading && (
<span className="progress-bar" style={{ width: `${progress}%` }}></span>
)}
</button>
)}
</div>
</div>
</div>
</div>
<VinoCreadoModal
isVisible={!!vinoCreado}
{...vinoCreado}
onClose={() => setVinoCreado(null)}
onVerStock={() => navigate('/stock', { state: { categoriaInicial: 'Depósito' } })}
/>
</>
);
};
function DocDetail() {
const { id } = useParams();
const [doc, setDoc] = useState(null);
const [loading, setLoading] = useState(true);
const navigate = useNavigate();
const [activeVinTab, setActiveVinTab] = useState('primera'); 
const location = useLocation();
const { fromStock } = location.state || {};
// Al abrir una trazabilidad se arranca directo en "Etiqueta" (la última
// pestaña): es el paso que falta resolver la mayoría de las veces que se
// entra a ver un vino.
const [activeTrazTab, setActiveTrazTab] = useState('etiqueta');
const [activePartidaIndex, setActivePartidaIndex] = useState(0);
const [activeSubTab, setActiveSubTab] = useState("primaria");
// Todos los valores de volumen se guardan en hectolitros; este toggle solo cambia cómo se muestran.
const [unidadVolumenDetalle, setUnidadVolumenDetalle] = useState('Hectolitros');
// Cuántas botellas etiquetar de cada línea del embotellado (índice -> texto
// del input). Se resetea después de etiquetar con éxito.
const [cantidadesEtiquetar, setCantidadesEtiquetar] = useState({});
const [etiquetando, setEtiquetando] = useState(false);
const [etiquetarError, setEtiquetarError] = useState(null);
const [etiquetarProgress, setEtiquetarProgress] = useState(0);
const formatHl = (valorHl) => {
if (valorHl === null || valorHl === undefined || valorHl === '' || isNaN(valorHl)) return '-';
const valorMostrado = unidadVolumenDetalle === 'Litros' ? valorHl * 100 : valorHl;
const unidad = unidadVolumenDetalle === 'Litros' ? 'lts' : 'hl';
return `${Number(valorMostrado).toLocaleString('es-AR')} ${unidad}`;
};
// Igual que formatHl pero sin la unidad al final, para armar la fracción
// "disponible / procesado hl" sin repetirla dos veces.
const formatHlNumero = (valorHl) => {
if (valorHl === null || valorHl === undefined || valorHl === '' || isNaN(valorHl)) return null;
const valorMostrado = unidadVolumenDetalle === 'Litros' ? valorHl * 100 : valorHl;
return Number(valorMostrado).toLocaleString('es-AR');
};
// El stock de vino a granel puede estar cargado en litros o en hectolitros;
// esto lo lleva siempre a hl para poder mostrarlo con formatHl().
const aHectolitrosSegunUnidad = (cantidad, tipoUnidad) => {
const cant = Number(cantidad);
if (cantidad === null || cantidad === undefined || isNaN(cant)) return null;
return tipoUnidad === 'Litro' ? cant / 100 : cant;
};
const formatearFecha = (timestamp) => {
if (!timestamp) return '-';
let dateObj;
if (timestamp.toDate) {
dateObj = timestamp.toDate();
} else if (timestamp.seconds) {
dateObj = new Date(timestamp.seconds * 1000);
} else {
return '-';
}
const dia = String(dateObj.getDate()).padStart(2, '0');
const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
const anio = dateObj.getFullYear();
const horas = String(dateObj.getHours()).padStart(2, '0');
const minutos = String(dateObj.getMinutes()).padStart(2, '0');
return `El ${dia}/${mes}/${anio} a las ${horas}:${minutos}`;
};
const fetchDoc = useCallback(async () => {
try {
// El prefijo del ID identifica la colección: lecturas puntuales (1-8 docs)
// en vez de descargar las 4 colecciones completas para buscar un documento.
const matchPrefijo = COLECCION_POR_PREFIJO.find(([prefijo]) => id.startsWith(prefijo));
let foundDoc = null;
if (matchPrefijo) {
const data = await fetchDocPuntual(matchPrefijo[1].nombre, id);
if (data) foundDoc = { ...data, coleccion: matchPrefijo[1].etiqueta };
}
// Fallback para IDs sin prefijo conocido: probar las demás colecciones,
// también con lecturas puntuales (máximo 4 reads, nunca colecciones enteras).
if (!foundDoc) {
for (const [, col] of COLECCION_POR_PREFIJO) {
if (matchPrefijo && col.nombre === matchPrefijo[1].nombre) continue;
const data = await fetchDocPuntual(col.nombre, id);
if (data) {
foundDoc = { ...data, coleccion: col.etiqueta };
break;
}
}
}
if (foundDoc && foundDoc.coleccion === 'Trazabilidad') {
if (foundDoc.embotellado_id) {
foundDoc.embotellado = await fetchDocPuntual('embotellado', foundDoc.embotellado_id);
}
const partidasConDatos = await Promise.all(
(foundDoc.partidas_usadas || []).map(async partida => {
// Vino a granel comprado no tiene partida de vinificación: 'ref' apunta
// directo al ítem de stock. Se trae ese doc para mostrar cuánto queda
// disponible HOY (no lo que había al momento de embotellar).
if (partida.origen === 'stock') {
const stockDoc = partida.ref ? await fetchDocPuntual('stock', partida.ref) : null;
// "Cantidad comprada" no se guarda aparte: se suma directo de las
// compras reales de este mismo ítem (mismo nombre y proveedor), que es
// donde ya vive el dato y no puede desincronizarse.
let totalComprado = null;
if (stockDoc?.nombre && stockDoc?.proveedorAsociado) {
const comprasSnap = await getDocs(
query(collection(db, 'compras'), where('cuitProveedor', '==', stockDoc.proveedorAsociado))
);
totalComprado = 0;
comprasSnap.forEach(d => {
(d.data().articulos || []).forEach(a => {
if (a.nombre === stockDoc.nombre) {
totalComprado += Number(a.cantidadUnidades) || 0;
}
});
});
}
return { ...partida, stockActual: stockDoc, totalComprado };
}
const partidaDoc = partida.ref ? await fetchDocPuntual('vinificacion', partida.ref) : null;
const viticulturaDoc = partidaDoc?.lote_origen_ref
? await fetchDocPuntual('viticultura', partidaDoc.lote_origen_ref)
: null;
return {
...partida,
detalles: partidaDoc,
viticultura: viticulturaDoc
};
})
);
foundDoc.partidas_usadas = partidasConDatos;
}
if (foundDoc && foundDoc.coleccion === 'Viticultura') {
// Un mismo lote puede haberse consumido en más de una vinificación (varias
// tiradas a lo largo del tiempo). Se buscan todas para linkearlas debajo de
// los quintales, con cuánto se usó en cada una.
// 'lote_origen_ref' en 'vinificacion' es un string en los docs nuevos, pero
// en algunos viejos quedó guardado como DocumentReference: un 'where' por
// igualdad de string no los encuentra, así que se trae todo y se filtra acá
// (misma lógica que ya usa el backend en /api/partidas).
const todasVinificaciones = await getDocs(collection(db, 'vinificacion'));
foundDoc.vinificaciones_que_lo_usan = todasVinificaciones.docs
.map(d => ({ id: d.id, data: d.data() }))
.filter(({ data }) => {
const ref = data.lote_origen_ref;
if (!ref) return false;
return typeof ref === 'string' ? ref === id : ref.id === id;
})
.map(({ id: vinId, data }) => ({
id: vinId,
quintales_consumidos: data.quintales_consumidos,
}));
}
if (foundDoc && foundDoc.coleccion === 'Vinificacion') {
// Misma idea que con viticultura: qué embotellados consumieron hectolitros
// de esta partida. 'partidas_usadas' es un array de mapas dentro del doc de
// embotellado, así que no se puede filtrar con un 'where' de Firestore: se
// trae todo y se filtra acá (colección chica, mismo criterio que arriba).
const todosEmbotellados = await getDocs(collection(db, 'embotellado'));
foundDoc.embotellados_que_lo_usan = todosEmbotellados.docs
.map(d => ({ id: d.id, data: d.data() }))
.map(({ id: botId, data }) => {
const lineas = (data.partidas_usadas || []).filter(
p => (p.origen || 'vinificacion') === 'vinificacion' && p.ref === id
);
if (lineas.length === 0) return null;
const cantidad_hl = lineas.reduce((sum, l) => sum + (Number(l.cantidad_hl) || 0), 0);
return { id: botId, cantidad_hl };
})
.filter(Boolean);
}
if (foundDoc && foundDoc.coleccion === 'Embotellado') {
// La etiqueta (el vino final) no se crea junto con el embotellado: se carga
// después desde la trazabilidad ('crear-vinos'). Acá 'embotellado_id' y
// 'trazabilidad_id' sí son campos planos (no arrays), así que van con 'where'.
const trazSnap = await getDocs(
query(collection(db, 'trazabilidad'), where('embotellado_id', '==', id))
);
const trazIds = trazSnap.docs.map(d => d.id);
if (trazIds.length > 0) {
const vinosSnap = await getDocs(
query(collection(db, 'vinos'), where('trazabilidad_id', 'in', trazIds))
);
const nombres = vinosSnap.docs.map(d => d.data().vino_nombre).filter(Boolean);
foundDoc.vinos_creados = Array.from(new Set(nombres));
}
// Qué trazabilidad etiquetó cada línea (y cuánto): para el tooltip sobre
// "Cantidad de botellas", mismo patrón que usa Viticultura/Vinificación con
// UsosTooltip. 'linea_index' es el índice dentro de este mismo embotellado
// (ver etiquetar_embotellado en el backend).
const usosPorLinea = {};
trazSnap.docs.forEach(d => {
(d.data().embotellados_detalle || []).forEach(detalle => {
const idx = detalle.linea_index;
if (idx === undefined || idx === null) return;
if (!usosPorLinea[idx]) usosPorLinea[idx] = [];
usosPorLinea[idx].push({ id: d.id, cantidad_botellas: detalle.cantidad_botellas });
});
});
// Cuánto de cada línea sigue sin etiquetar: vive en 'stock' bajo
// '{embotellado_id}-L{índice}' (ver comentario en crear_embotellado /
// etiquetar_embotellado). Se muestra al lado del input de "a etiquetar"
// para no dejar pedir más de lo que realmente queda.
const detalles = foundDoc.embotellados_detalle || [];
foundDoc.embotellados_detalle = await Promise.all(
detalles.map(async (detalle, index) => {
const stockSinEtiquetar = await fetchDocPuntual('stock', `${id}-L${index}`);
return {
...detalle,
disponibleParaEtiquetar: Number(stockSinEtiquetar?.cantidadUnidades) || 0,
usos: usosPorLinea[index] || [],
};
})
);
}
setDoc(foundDoc || null);
} catch (err) {
console.error(err);
} finally {
setLoading(false);
}
}, [id]);
useEffect(() => {
fetchDoc();
}, [fetchDoc]);
const handleEdit = (coleccion, id) => {
navigate(`/trazabilidad/${coleccion}/${id}/edit`);
};
const handleEtiquetar = async () => {
const lineas = Object.entries(cantidadesEtiquetar)
.map(([index, valor]) => ({ index: parseInt(index, 10), cantidad_botellas: parseInt(valor, 10) || 0 }))
.filter(l => l.cantidad_botellas > 0);
if (lineas.length === 0) {
setEtiquetarError('Cargá la cantidad de botellas a etiquetar de al menos una línea.');
return;
}
setEtiquetando(true);
setEtiquetarError(null);
setEtiquetarProgress(0);
const interval = setInterval(() => {
setEtiquetarProgress(prev => Math.min(prev + 5, 95));
}, 100);
try {
const user = getAuth().currentUser;
if (!user) throw new Error('Debés iniciar sesión.');
const idToken = await user.getIdToken();
const response = await fetch(`https://backend-288175208699.europe-west1.run.app/api/embotellado/${doc.id}/etiquetar`, {
method: 'POST',
headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
body: JSON.stringify({ lineas }),
});
clearInterval(interval);
setEtiquetarProgress(100);
const data = await response.json();
if (!response.ok) throw new Error(data.error || 'Ocurrió un error.');
setCantidadesEtiquetar({});
navigate(`/trazabilidad/${data.trazabilidad_creada}`);
} catch (error) {
clearInterval(interval);
setEtiquetarError(error.message);
setEtiquetando(false);
setEtiquetarProgress(0);
}
};
const vinTabs = [
{ id: 'primera', label: 'Primera fermentación' },
{ id: 'segunda', label: 'Segunda fermentación' },
{ id: 'tranquilizacion', label: 'Tranquilización' },
{ id: 'guarda', label: 'Guarda' },
];
// Si es 100% vino comprado no hay ninguna partida con fermentación real:
// "Vinificación" quedaría siempre vacía. En ese caso se unifica con
// "Viticultura" en una sola pestaña (mismo contenido de "Viticultura", que
// ya sabe mostrar los datos del vino comprado, pero con el nombre
// "Vinificación"). Si hay aunque sea una partida propia, quedan las 4
// pestañas separadas como siempre, porque esa partida sí tiene fermentación
// real para mostrar.
const todoVinoComprado = doc?.coleccion === 'Trazabilidad' &&
doc?.partidas_usadas?.length > 0 &&
doc.partidas_usadas.every(p => p.origen === 'stock');
const trazTabs = todoVinoComprado
? [
{ id: 'viticultura', label: 'Vinificación' },
{ id: 'embotellado', label: 'Embotellado' },
{ id: 'etiqueta', label: 'Etiqueta' }
]
: [
{ id: 'viticultura', label: 'Viticultura' },
{ id: 'vinificacion', label: 'Vinificación' },
{ id: 'embotellado', label: 'Embotellado' },
{ id: 'etiqueta', label: 'Etiqueta' }
];
const renderVinTabContent = () => {
switch (activeVinTab) {
case 'primera':
return (
<div className="columns-container" >
<div className="data-column long">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{doc.profesional_responsable ? doc.profesional_responsable : '-'}</p>
<label className="data-label">Lote de Origen</label>
<p className="data-p link" onClick={()=>{navigate(`/trazabilidad/${doc.lote_origen_ref}`);}}>
{doc.lote_origen_ref ? doc.lote_origen_ref : '-'}
</p>
<label className="data-label">Hectolitros</label>
<UsosTooltip
usos={doc.embotellados_que_lo_usan}
navigate={navigate}
formatPrefijo={(e) => e.cantidad_hl ? `${formatHl(e.cantidad_hl)} en ` : 'en '}
>
<p className="data-p">
{doc.hectolitros_procesados
? `${formatHlNumero(doc.hectolitros_presentes ?? doc.hectolitros_procesados)} / ${formatHlNumero(doc.hectolitros_procesados)} ${unidadVolumenDetalle === 'Litros' ? 'lts' : 'hl'}`
: '-'}
</p>
</UsosTooltip>
<label className="data-label">Tipo de uso</label>
<p className="data-p">{doc.tipo_uso ? doc.tipo_uso : '-'}</p>
<label className="data-label">Fecha de inicio</label>
<p className="data-p">
{doc.fermentacion_primaria.inicio
? doc.fermentacion_primaria.inicio.split('-').reverse().join('/')
: '-'}
</p>
<label className="data-label">Recipiente utilizado</label>
<p className="data-p">
{doc.fermentacion_primaria.recipiente_utilizado ? doc.fermentacion_primaria.recipiente_utilizado : '-'}
</p>
<label className="data-label">Temperatura</label>
<p className="data-p">
{doc.fermentacion_primaria.temperatura ? `${doc.fermentacion_primaria.temperatura}° C` : '-'}
</p>
<label className="data-label">Humedad</label>
<p className="data-p">
{doc.fermentacion_primaria.humedad ? `${doc.fermentacion_primaria.humedad}%` : '-'}
</p>
<label className="data-label">Tipo de levadura</label>
<p className="data-p">
{doc.tipo_levadura ? doc.tipo_levadura : '-'}
</p>
</div>
<div className="data-column">
<label className="data-label">Graduación alcohólica</label>
<p className="data-p">
{doc.fermentacion_primaria.graduacion_alcoholica ? `${doc.fermentacion_primaria.graduacion_alcoholica}%` : '-'}
</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">
{doc.fermentacion_primaria.nivel_acidez ? `${doc.fermentacion_primaria.nivel_acidez} g/L` : '-'}
</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">
{doc.fermentacion_primaria.nivel_azucar ? `${doc.fermentacion_primaria.nivel_azucar}° Brix` : '-'}
</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">
{doc.fermentacion_primaria.nivel_ph ? doc.fermentacion_primaria.nivel_ph : '-'}
</p>
<label className="data-label">Fecha de finalización</label>
<p className="data-p">
{doc.fermentacion_primaria.fin
? doc.fermentacion_primaria.fin.split('-').reverse().join('/')
: '-'}
</p>
</div>
<div className="data-column long">
<div className="gray-box">
<label className="data-label">Cantidad de días totales</label>
<p className="data-p">
{doc.dias_fermentacion_primaria ? `${doc.dias_fermentacion_primaria}d` : '-'}
</p>
</div>
<div className="button-container" style={{gap:'10px'}}>
<label className="data-label" style={{margin:'0'}}>Precio unitario</label>
<p className="data-p" style={{margin:'0'}}>{doc.precioUnidad ? `$ ${doc.precioUnidad.toLocaleString('es-AR')}` : '-'}</p>
<hr style={{ border: "0", borderTop: "1px solid #eaeef5", margin: "0px 0 0px" }} />
<button className="button"
onClick={() => handleEdit('vinificacion', doc.id)}
>Editar</button>
</div>
</div>
</div>
);
case 'segunda':
return (
<div className="columns-container" >
<div className="data-column">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{doc.profesional_responsable ? doc.profesional_responsable : '-'}</p>
<label className="data-label">Lote de Origen</label>
<p className="data-p link" onClick={()=>{navigate(`/trazabilidad/${doc.lote_origen_ref}`);}}>
{doc.lote_origen_ref ? doc.lote_origen_ref : '-'}
</p>
<label className="data-label">Hectolitros</label>
<UsosTooltip
usos={doc.embotellados_que_lo_usan}
navigate={navigate}
formatPrefijo={(e) => e.cantidad_hl ? `${formatHl(e.cantidad_hl)} en ` : 'en '}
>
<p className="data-p">
{doc.hectolitros_procesados
? `${formatHlNumero(doc.hectolitros_presentes ?? doc.hectolitros_procesados)} / ${formatHlNumero(doc.hectolitros_procesados)} ${unidadVolumenDetalle === 'Litros' ? 'lts' : 'hl'}`
: '-'}
</p>
</UsosTooltip>
<label className="data-label">Tipo de uso</label>
<p className="data-p">{doc.tipo_uso ? doc.tipo_uso : '-'}</p>
<label className="data-label">Fecha de inicio</label>
<p className="data-p">
{doc.fermentacion_secundaria.inicio
? doc.fermentacion_secundaria.inicio.split('-').reverse().join('/')
: '-'}
</p>
<label className="data-label">Recipiente utilizado</label>
<p className="data-p">
{doc.fermentacion_secundaria.recipiente_utilizado ? doc.fermentacion_secundaria.recipiente_utilizado : '-'}
</p>
<label className="data-label">Temperatura</label>
<p className="data-p">
{doc.fermentacion_secundaria.temperatura ? `${doc.fermentacion_secundaria.temperatura}° C` : '-'}
</p>
<label className="data-label">Humedad</label>
<p className="data-p">
{doc.fermentacion_secundaria.humedad ? `${doc.fermentacion_secundaria.humedad}%` : '-'}
</p>
</div>
<div className="data-column">
<label className="data-label">Graduación alcohólica</label>
<p className="data-p">
{doc.fermentacion_secundaria.graduacion_alcoholica ? `${doc.fermentacion_secundaria.graduacion_alcoholica}%` : '-'}
</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">
{doc.fermentacion_secundaria.nivel_acidez ? `${doc.fermentacion_secundaria.nivel_acidez} g/L` : '-'}
</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">
{doc.fermentacion_secundaria.nivel_azucar ? `${doc.fermentacion_secundaria.nivel_azucar}° Brix` : '-'}
</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">
{doc.fermentacion_secundaria.nivel_ph ? doc.fermentacion_secundaria.nivel_ph : '-'}
</p>
<label className="data-label">Fecha de finalización</label>
<p className="data-p">
{doc.fermentacion_secundaria.fin
? doc.fermentacion_secundaria.fin.split('-').reverse().join('/')
: '-'}
</p>
</div>
<div className="data-column long">
<div className="gray-box">
<label className="data-label">Cantidad de días totales</label>
<p className="data-p">
{doc.dias_fermentacion_secundaria ? `${doc.dias_fermentacion_secundaria}d` : '-'}
</p>
</div>
<div className="button-container" style={{gap:'10px'}}>
<label className="data-label" style={{margin:'0'}}>Precio unitario</label>
<p className="data-p" style={{margin:'0'}}>{doc.precioUnidad ? `$ ${doc.precioUnidad.toLocaleString('es-AR')}` : '-'}</p>
<hr style={{ border: "0", borderTop: "1px solid #eaeef5", margin: "0px 0 0px" }} />
<button className="button"
onClick={() => handleEdit('vinificacion', doc.id)}
>Editar</button>
</div>
</div>
</div>
);
case 'tranquilizacion':
return (
<div className="columns-container" >
<div className="data-column">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{doc.profesional_responsable ? doc.profesional_responsable : '-'}</p>
<label className="data-label">Lote de Origen</label>
<p className="data-p link" onClick={()=>{navigate(`/trazabilidad/${doc.lote_origen_ref}`);}}>
{doc.lote_origen_ref ? doc.lote_origen_ref : '-'}
</p>
<label className="data-label">Hectolitros</label>
<UsosTooltip
usos={doc.embotellados_que_lo_usan}
navigate={navigate}
formatPrefijo={(e) => e.cantidad_hl ? `${formatHl(e.cantidad_hl)} en ` : 'en '}
>
<p className="data-p">
{doc.hectolitros_procesados
? `${formatHlNumero(doc.hectolitros_presentes ?? doc.hectolitros_procesados)} / ${formatHlNumero(doc.hectolitros_procesados)} ${unidadVolumenDetalle === 'Litros' ? 'lts' : 'hl'}`
: '-'}
</p>
</UsosTooltip>
<label className="data-label">Tipo de uso</label>
<p className="data-p">{doc.tipo_uso ? doc.tipo_uso : '-'}</p>
<label className="data-label">Fecha de inicio</label>
<p className="data-p">
{doc.tranquilizacion.inicio
? doc.tranquilizacion.inicio.split('-').reverse().join('/')
: '-'}
</p>
<label className="data-label">Recipiente utilizado</label>
<p className="data-p">
{doc.tranquilizacion.recipiente_utilizado ? doc.tranquilizacion.recipiente_utilizado : '-'}
</p>
<label className="data-label">Temperatura</label>
<p className="data-p">
{doc.tranquilizacion.temperatura ? `${doc.tranquilizacion.temperatura}° C` : '-'}
</p>
<label className="data-label">Humedad</label>
<p className="data-p">
{doc.tranquilizacion.humedad ? `${doc.tranquilizacion.humedad}%` : '-'}
</p>
</div>
<div className="data-column">
<label className="data-label">Graduación alcohólica</label>
<p className="data-p">
{doc.tranquilizacion.graduacion_alcoholica ? `${doc.tranquilizacion.graduacion_alcoholica}%` : '-'}
</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">
{doc.tranquilizacion.nivel_acidez ? `${doc.tranquilizacion.nivel_acidez} g/L` : '-'}
</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">
{doc.tranquilizacion.nivel_azucar ? `${doc.tranquilizacion.nivel_azucar}° Brix` : '-'}
</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">
{doc.tranquilizacion.nivel_ph ? doc.tranquilizacion.nivel_ph : '-'}
</p>
<label className="data-label">Fecha de finalización</label>
<p className="data-p">
{doc.tranquilizacion.fin
? doc.tranquilizacion.fin.split('-').reverse().join('/')
: '-'}
</p>
</div>
<div className="data-column long">
<div className="gray-box">
<label className="data-label">Cantidad de días totales</label>
<p className="data-p">
{doc.dias_tranquilizacion ? `${doc.dias_tranquilizacion}d` : '-'}
</p>
</div>
<div className="button-container" style={{gap:'10px'}}>
<label className="data-label" style={{margin:'0'}}>Precio unitario</label>
<p className="data-p" style={{margin:'0'}}>{doc.precioUnidad ? `$ ${doc.precioUnidad.toLocaleString('es-AR')}` : '-'}</p>
<hr style={{ border: "0", borderTop: "1px solid #eaeef5", margin: "0px 0 0px" }} />
<button className="button"
onClick={() => handleEdit('vinificacion', doc.id)}
>Editar</button>
</div>
</div>
</div>
);
case 'guarda':
return (
<div className="columns-container" >
<div className="data-column">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{doc.profesional_responsable ? doc.profesional_responsable : '-'}</p>
<label className="data-label">Lote de Origen</label>
<p className="data-p link" onClick={()=>{navigate(`/trazabilidad/${doc.lote_origen_ref}`);}}>
{doc.lote_origen_ref ? doc.lote_origen_ref : '-'}
</p>
<label className="data-label">Hectolitros</label>
<UsosTooltip
usos={doc.embotellados_que_lo_usan}
navigate={navigate}
formatPrefijo={(e) => e.cantidad_hl ? `${formatHl(e.cantidad_hl)} en ` : 'en '}
>
<p className="data-p">
{doc.hectolitros_procesados
? `${formatHlNumero(doc.hectolitros_presentes ?? doc.hectolitros_procesados)} / ${formatHlNumero(doc.hectolitros_procesados)} ${unidadVolumenDetalle === 'Litros' ? 'lts' : 'hl'}`
: '-'}
</p>
</UsosTooltip>
<label className="data-label">Tipo de uso</label>
<p className="data-p">{doc.tipo_uso ? doc.tipo_uso : '-'}</p>
<label className="data-label">Fecha de inicio</label>
<p className="data-p">
{doc.guarda.inicio
? doc.guarda.inicio.split('-').reverse().join('/')
: '-'}
</p>
<label className="data-label">Recipiente utilizado</label>
<p className="data-p">
{doc.guarda.recipiente_utilizado ? doc.guarda.recipiente_utilizado : '-'}
</p>
<label className="data-label">Temperatura</label>
<p className="data-p">
{doc.guarda.temperatura ? `${doc.guarda.temperatura}° C` : '-'}
</p>
<label className="data-label">Humedad</label>
<p className="data-p">
{doc.guarda.humedad ? `${doc.guarda.humedad}%` : '-'}
</p>
</div>
<div className="data-column">
<label className="data-label">Graduación alcohólica</label>
<p className="data-p">
{doc.guarda.graduacion_alcoholica ? `${doc.guarda.graduacion_alcoholica}%` : '-'}
</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">
{doc.guarda.nivel_acidez ? `${doc.guarda.nivel_acidez} g/L` : '-'}
</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">
{doc.guarda.nivel_azucar ? `${doc.guarda.nivel_azucar}° Brix` : '-'}
</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">
{doc.guarda.nivel_ph ? doc.guarda.nivel_ph : '-'}
</p>
<label className="data-label">Fecha de finalización</label>
<p className="data-p">
{doc.guarda.fin
? doc.guarda.fin.split('-').reverse().join('/')
: '-'}
</p>
</div>
<div className="data-column long">
<div className="gray-box">
<label className="data-label">Cantidad de días totales</label>
<p className="data-p">
{doc.dias_guarda ? `${doc.dias_guarda}d` : '-'}
</p>
</div>
<div className="button-container" style={{gap:'10px'}}>
<label className="data-label" style={{margin:'0'}}>Precio unitario</label>
<p className="data-p" style={{margin:'0'}}>{doc.precioUnidad ? `$ ${doc.precioUnidad.toLocaleString('es-AR')}` : '-'}</p>
<hr style={{ border: "0", borderTop: "1px solid #eaeef5", margin: "0px 0 0px" }} />
<button className="button"
onClick={() => handleEdit('vinificacion', doc.id)}
>Editar</button>
</div>
</div>
</div>
);
default:
return null;
}
};
const renderTrazTabContent = () => {
switch (activeTrazTab) {
case 'viticultura':
return (
<div className="doc-detail viticultura-detail">
{/* Sin sentido un selector para elegir entre una sola partida — antes
    igual se renderizaba con 1 sola opción, y para vino comprado (que ya
    no muestra el nombre acá arriba) quedaba una fila vacía ocupando
    espacio. */}
{doc?.partidas_usadas?.length > 1 && (
<div className="tabs-container"
style={{ display: "flex", gap: "30px", margin: " 10px 5px 15px" }}>
{doc?.partidas_usadas?.map((p, index) => (
<button
// key por índice: dos partidas de la misma cepa compradas a distintos
// proveedores pueden compartir nombre_stock (ej. "Vino Malbec" x2), y un
// key duplicado entre hermanos confunde a React al re-renderizar
// (aparecían botones fantasma duplicados).
key={index}
style={{
background: "none",
border: "none",
padding: "0",fontSize:'17px',fontFamily:'Effra', fontWeight:'400',
color: index === activePartidaIndex ? "#e65c5c" : "#b3b3b3",
cursor: "pointer",
}}
onClick={() => setActivePartidaIndex(index)}
>
{/* Para vino comprado se usa la bodega proveedora como etiqueta del
    selector — antes quedaba en blanco (pensado para cuando hay una sola
    partida comprada, donde el "Vino comprado a granel" de abajo ya alcanza),
    pero con 2+ partidas compradas de la misma cepa a proveedores distintos
    (mismo blend) los botones quedaban indistinguibles entre sí. */}
{p.origen === 'stock' ? (p.bodega || p.nombre_stock || 'Sin bodega') : (p.detalles?.lote_origen_ref || p.nombre_stock || 'Sin lote')}
</button>
))}
</div>
)}
<div className="partida-detail">
{doc?.partidas_usadas?.[activePartidaIndex] && (() => {
const partida = doc.partidas_usadas[activePartidaIndex];
// Vino a granel comprado: no viene de un lote propio, no tiene viticultura
// detrás. Se muestra lo que sí se cargó al embotellar en vez de romper
// intentando leer campos de un objeto que no existe.
if (partida.origen === 'stock') {
return (
<div className="doc-detail viticultura-detail">
<div className="columns-container" style={{ display: 'flex', gap: '40px', paddingTop: '10px' }}>
<div className="data-column long">
<label className="data-label">Vino comprado a granel</label>
<p
className="data-p link"
onClick={() => navigate('/stock', { state: { categoriaInicial: 'Insumos', itemDestacado: partida.ref } })}
>
{mostrar(partida.nombre_stock)}
</p>
<label className="data-label">Cepa</label>
<p className="data-p">{mostrar(partida.cepa)}</p>
<label className="data-label">Bodega de origen</label>
<p className="data-p">{mostrar(partida.bodega)}</p>
<label className="data-label">Cantidad utilizada</label>
<p className="data-p">{formatHl(partida.cantidad_hl)}</p>
</div>
<div className="data-column">
<label className="data-label">Cantidad comprada</label>
<p className="data-p">{formatHl(aHectolitrosSegunUnidad(partida.totalComprado, partida.stockActual?.tipoUnidad))}</p>
<label className="data-label">Cantidad actual en stock</label>
<p className="data-p">{formatHl(aHectolitrosSegunUnidad(partida.stockActual?.cantidadUnidades, partida.stockActual?.tipoUnidad))}</p>
</div>
{/* Columna vacía a propósito: mantiene la estructura de 3 columnas (igual
    que la vista de partida propia) para que no quede un hueco grande en
    el medio con solo 2. */}
<div className="data-column"></div>
</div>
</div>
);
}
return (
<div className="doc-detail viticultura-detail">
<div className="columns-container" style={{ display: 'flex', gap: '40px', paddingTop: '10px' }}>
<div className="data-column long">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{mostrar(partida.detalles?.profesional_responsable)}</p>
<label className="data-label">Tipo de propiedad</label>
<p className="data-p" style={{textTransform: 'capitalize'}}>{mostrar(partida.viticultura?.pertenencia)}</p>
{partida.viticultura?.pertenencia?.toLowerCase() === 'propia' && (
<>
<label className="data-label">Área de cosecha</label>
<p className="data-p" style={{textTransform:'capitalize'}}>
{partida.viticultura?.provenienza?.toLowerCase() === 'hectarea'
? 'Hectárea'
: mostrar(partida.viticultura?.provenienza)}
</p>
</>
)}
{partida.viticultura?.pertenencia?.toLowerCase() === 'tercero' && (
<>
<label className="data-label">Nombre de la finca</label>
<p className="data-p">{mostrar(partida.viticultura?.nombre_finca)}</p>
</>
)}
<label className="data-label">Tipo de suelo</label>
<p className="data-p">{mostrar(partida.viticultura?.tipo_suelo)}</p>
<label className="data-label">Tipo de riego</label>
<p className="data-p">{mostrar(partida.viticultura?.tipo_riego)}</p>
<label className="data-label">Altura sobre el nivel del mar</label>
<p className="data-p">{mostrar(partida.viticultura?.altura_snm)}m</p>
<label className="data-label">Fecha de cosecha</label>
<p className="data-p">{mostrarFecha(partida.viticultura?.fecha_cosecha)}</p>
</div>
<div className="data-column">
<label className="data-label">Fecha de cosecha</label>
<p className="data-p">{mostrarFecha(partida.viticultura?.fecha_cosecha)}</p>
<label className="data-label">Cepa</label>
<p className="data-p">{mostrar(partida.viticultura?.variedad_uva)}</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">{mostrar(partida.viticultura?.nivel_acido)} g/L</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">{mostrar(partida.viticultura?.nivel_azucar)}° Brix</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">{mostrar(partida.viticultura?.nivel_ph)}</p>
<label className="data-label">Quintales</label>
<p className="data-p">{mostrar(partida.viticultura?.quintales)} q</p>
</div>
<div className="data-column">
<label className="data-label">Días de sol</label>
<p className="data-p">{mostrar(partida.viticultura?.dias_sol)} d</p>
<label className="data-label">Precipitación media anual</label>
<p className="data-p">{mostrar(partida.viticultura?.precipitacion_media_anual)} mm</p>
<label className="data-label">Temperatura media anual</label>
<p className="data-p">{mostrar(partida.viticultura?.temperatura_media_anual)}° C</p>
<label className="data-label">Humedad media anual</label>
<p className="data-p">{mostrar(partida.viticultura?.humedad_media_anual)}%</p>
</div>
</div>
</div>
);
})()}
</div>
</div>
);
case 'vinificacion':
return (
<div>
<div
className="tabs-container"
style={{ display: "flex", gap: "30px", margin: " 10px 5px 15px" }}
>
{doc?.partidas_usadas?.map((p, index) => (
<button
key={index}
onClick={() => setActivePartidaIndex(index)}
style={{
background: "none",
border: "none",
padding: "0",fontSize:'17px',fontFamily:'Effra', fontWeight:'400',
color: index === activePartidaIndex ? "#e65c5c" : "#b3b3b3",
cursor: "pointer",
}}
>
{p.ref}
</button>
))}
</div>
<ul className="tabs-nav">
{["primaria", "secundaria", "tranquilizacion", "guarda"].map((tab) => (
<li
key={tab}
className={activeSubTab === tab ? "active" : ""}
>
<a
href="#"
onClick={(e) => {
e.preventDefault();
setActiveSubTab(tab);
}}
>
{tab === "primaria" && "Primera fermentación"}
{tab === "secundaria" && "Segunda fermentación"}
{tab === "tranquilizacion" && "Tranquilización"}
{tab === "guarda" && "Guarda"}
</a>
</li>
))}
</ul>
{doc?.partidas_usadas?.[activePartidaIndex] && (() => {
const partida = doc.partidas_usadas[activePartidaIndex];
const fPrim = partida.detalles?.fermentacion_primaria;
const fSec = partida.detalles?.fermentacion_secundaria;
const tranq = partida.detalles?.tranquilizacion;
const guarda = partida.detalles?.guarda;
switch (activeSubTab) {
case "primaria":
return fPrim ? (
<div className="columns-container" style={{paddingTop:'20px'}} >
<div className="data-column long">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{mostrar(partida.detalles?.profesional_responsable)}</p>
<label className="data-label">Tipo de uso</label>
<p className="data-p">
  {partida.detalles?.tipo_uso}
</p>
<label className="data-label">Lote de Origen</label>
<p className="data-p link" onClick={()=>{navigate(`/trazabilidad/${partida.detalles?.lote_origen_ref}`);}}>
  {partida.detalles?.lote_origen_ref}
</p>
<label className="data-label">Cantidad de hectolitros procesados</label>
<p className="data-p">
  {partida.detalles?.hectolitros_procesados} hl
</p>
<label className="data-label">Cantidad de hectolitros disponibles</label>
<p className="data-p">
  {partida.detalles?.hectolitros_presentes} hl
</p>
<label className="data-label">Fecha de inicio</label>
<p className="data-p">
{fPrim.inicio
? fPrim.inicio.split('-').reverse().join('/')
: ''}
</p>
<label className="data-label">Recipiente utilizado</label>
<p className="data-p">
  {fPrim.recipiente_utilizado}
</p>
<label className="data-label">Temperatura</label>
<p className="data-p">
  {fPrim.temperatura}° C
</p>
<label className="data-label">Humedad</label>
<p className="data-p">
  {fPrim.humedad}%
</p>
<label className="data-label">Tipo de levadura</label>
<p className="data-p">
  {partida.detalles?.tipo_levadura}
</p>
</div>
<div className="data-column">
<label className="data-label">Graduación alcohólica</label>
<p className="data-p">
  {fPrim.graduacion_alcoholica}%
</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">
  {fPrim.nivel_acidez} g/L
</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">
  {fPrim.nivel_azucar}° Brix
</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">
  {fPrim.nivel_ph}
</p>
<label className="data-label">Fecha de finalización</label>
<p className="data-p">
{fPrim.fin
? fPrim.fin.split('-').reverse().join('/')
: ''}
</p>
</div>
<div className="data-column long">
<div className="gray-box">
<label className="data-label">Cantidad de días totales</label>
<p className="data-p">
{partida.detalles?.dias_fermentacion_primaria}d
</p>
</div>
</div>
</div>
) : <p>No hay datos de fermentación primaria</p>;
case "secundaria":
return fSec ? (
<div className="columns-container" style={{paddingTop:'20px'}}>
<div className="data-column long">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{mostrar(partida.detalles?.profesional_responsable)}</p>
<label className="data-label">Tipo de uso</label>
<p className="data-p">
  {partida.detalles?.tipo_uso}
</p>
<label className="data-label">Lote de Origen</label>
<p className="data-p link" onClick={()=>{navigate(`/trazabilidad/${partida.detalles?.lote_origen_ref}`);}}>
  {partida.detalles?.lote_origen_ref}
</p>
<label className="data-label">Cantidad de hectolitros procesados</label>
<p className="data-p">
  {partida.detalles?.hectolitros_procesados} hl
</p>
<label className="data-label">Cantidad de hectolitros disponibles</label>
<p className="data-p">
  {partida.detalles?.hectolitros_presentes} hl
</p>
<label className="data-label">Fecha de inicio</label>
<p className="data-p">
{fSec.inicio
? fSec.inicio.split('-').reverse().join('/')
: ''}
</p>
<label className="data-label">Recipiente utilizado</label>
<p className="data-p">
  {fSec.recipiente_utilizado}
</p>
<label className="data-label">Temperatura</label>
<p className="data-p">
  {fSec.temperatura}° C
</p>
<label className="data-label">Humedad</label>
<p className="data-p">
  {fSec.humedad}%
</p>
</div>
<div className="data-column">
<label className="data-label">Graduación alcohólica</label>
<p className="data-p">
  {fSec.graduacion_alcoholica}%
</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">
  {fSec.nivel_acidez} g/L
</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">
  {fSec.nivel_azucar}° Brix
</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">
  {fSec.nivel_ph}
</p>
<label className="data-label">Fecha de finalización</label>
<p className="data-p">
{fSec.fin
? fSec.fin.split('-').reverse().join('/')
: ''}
</p>
</div>
<div className="data-column long">
<div className="gray-box">
<label className="data-label">Cantidad de días totales</label>
<p className="data-p">
{partida.detalles?.dias_fermentacion_secundaria}d
</p>
</div>
</div>
</div>
) : <p>No hay datos de fermentación secundaria</p>;
case "tranquilizacion":
return tranq ? (
<div className="columns-container" style={{paddingTop:'20px'}}>
<div className="data-column long">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{mostrar(partida.detalles?.profesional_responsable)}</p>
<label className="data-label">Tipo de uso</label>
<p className="data-p">
  {partida.detalles?.tipo_uso}
</p>
<label className="data-label">Lote de Origen</label>
<p className="data-p link" onClick={()=>{navigate(`/trazabilidad/${partida.detalles?.lote_origen_ref}`);}}>
  {partida.detalles?.lote_origen_ref}
</p>
<label className="data-label">Cantidad de hectolitros procesados</label>
<p className="data-p">
  {partida.detalles?.hectolitros_procesados} hl
</p>
<label className="data-label">Cantidad de hectolitros disponibles</label>
<p className="data-p">
  {partida.detalles?.hectolitros_presentes} hl
</p>
<label className="data-label">Fecha de inicio</label>
<p className="data-p">
{tranq.inicio
? tranq.inicio.split('-').reverse().join('/')
: ''}
</p>
<label className="data-label">Recipiente utilizado</label>
<p className="data-p">
  {tranq.recipiente_utilizado}
</p>
<label className="data-label">Temperatura</label>
<p className="data-p">
  {tranq.temperatura}° C
</p>
<label className="data-label">Humedad</label>
<p className="data-p">
  {tranq.humedad}%
</p>
</div>
<div className="data-column">
<label className="data-label">Graduación alcohólica</label>
<p className="data-p">
  {tranq.graduacion_alcoholica}%
</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">
  {tranq.nivel_acidez} g/L
</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">
  {tranq.nivel_azucar}° Brix
</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">
  {tranq.nivel_ph}
</p>
<label className="data-label">Fecha de finalización</label>
<p className="data-p">
{tranq.fin
? tranq.fin.split('-').reverse().join('/')
: ''}
</p>
</div>
<div className="data-column long">
<div className="gray-box">
<label className="data-label">Cantidad de días totales</label>
<p className="data-p">
{partida.detalles?.dias_tranquilizacion}d
</p>
</div>
</div>
</div>
) : <p>No hay datos de tranquilización</p>;
case "guarda":
return guarda ? (
<div className="columns-container" style={{paddingTop:'20px'}}>
<div className="data-column long">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{mostrar(partida.detalles?.profesional_responsable)}</p>
<label className="data-label">Tipo de uso</label>
<p className="data-p">
  {partida.detalles?.tipo_uso}
</p>
<label className="data-label">Lote de Origen</label>
<p className="data-p link" onClick={()=>{navigate(`/trazabilidad/${partida.detalles?.lote_origen_ref}`);}}>
  {partida.detalles?.lote_origen_ref}
</p>
<label className="data-label">Cantidad de hectolitros procesados</label>
<p className="data-p">
  {partida.detalles?.hectolitros_procesados} hl
</p>
<label className="data-label">Cantidad de hectolitros disponibles</label>
<p className="data-p">
  {partida.detalles?.hectolitros_presentes} hl
</p>
<label className="data-label">Fecha de inicio</label>
<p className="data-p">
{guarda.inicio
? guarda.inicio.split('-').reverse().join('/')
: ''}
</p>
<label className="data-label">Recipiente utilizado</label>
<p className="data-p">
  {guarda.recipiente_utilizado}
</p>
<label className="data-label">Temperatura</label>
<p className="data-p">
  {guarda.temperatura}° C
</p>
<label className="data-label">Humedad</label>
<p className="data-p">
  {guarda.humedad}%
</p>
</div>
<div className="data-column">
<label className="data-label">Graduación alcohólica</label>
<p className="data-p">
  {guarda.graduacion_alcoholica}%
</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">
  {guarda.nivel_acidez} g/L
</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">
  {guarda.nivel_azucar}° Brix
</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">
  {guarda.nivel_ph}
</p>
<label className="data-label">Fecha de finalización</label>
<p className="data-p">
{guarda.fin
? guarda.fin.split('-').reverse().join('/')
: ''}
</p>
</div>
<div className="data-column long">
<div className="gray-box">
<label className="data-label">Cantidad de días totales</label>
<p className="data-p">
{partida.detalles?.dias_guarda}d
</p>
</div>
</div>
</div>
) : <p>No hay datos de tranquilización</p>;
default:
return null;
}
})()}
</div>
);
case 'embotellado':
return (
<div className="doc-detail embotellado-detail">
{doc.embotellado && (
<>
<div className="columns-container" style={{ display: 'flex', gap: '40px', paddingTop:'10px' }}>
{/* .long agrega height:100vh al overflow:auto que .data-column ya tiene
    (App.css) — sin eso, overflow:auto no tiene una altura acotada contra
    la cual scrollear. Mismo patrón que la columna de Etiqueta/Cápsula/
    Cupo en el tab Etiqueta: con varias partidas, esta columna scrollea
    sola en vez de estirar toda la página. */}
<div className="data-column long">
<label className="data-label">Embotellado</label>
<p  className="data-p" onClick={() => { navigate(`/trazabilidad/${doc.embotellado.id}`); }} style={{fontSize: '18px', color: '#e65c5c', cursor:'pointer'}}>{mostrar(doc.embotellado.id)}</p>
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{mostrar(doc.embotellado.profesional_responsable)}</p>
<label className="data-label">Tipo de vino</label>
<p className="data-p" style={{ textTransform: 'capitalize' }}>
{mostrar(doc.embotellado.tipo_vino)}
</p>
<label className="data-label">Tipo de uso</label>
<p className="data-p" style={{ textTransform: 'capitalize' }}>
{mostrar(doc.embotellado.tipo_uso)}
</p>
{doc.embotellado.partidas_usadas && doc.embotellado.partidas_usadas.length > 0 ? (
doc.embotellado.partidas_usadas.map((p, index) => {
const idPartida = p.ref?.id || mostrar(p.ref);
return (
<> <hr style={{ border: "0", borderTop: "1px solid #ccc", margin: "0px 0 0px" }} />
<label className="data-label">Partida</label>
<p 
key={index} 
className="data-p link" 
style={{color: '#e65c5c'}} 
onClick={() => { navigate(`/trazabilidad/${idPartida}`); }}
>
{idPartida}
</p>
<label className="data-label">Cantidad de hectolitros usados</label>
<p className="data-p">{formatHl(p.cantidad_hl)}</p>
</>
);
})
) : (
<p className="data-p">No hay partidas registradas</p>
)}
</div>
<div className="data-column long">
{/* A propósito NO es doc.embotellados_detalle (la porción de esta
    trazabilidad): este tab es "Embotellado", muestra cuánto se
    embotelló físicamente en total. La porción etiquetada por esta
    trazabilidad se ve en el tab "Etiqueta". */}
{doc.embotellado?.embotellados_detalle?.length > 0 ? (
doc.embotellado.embotellados_detalle.map((p, index) => (
<React.Fragment key={index}>
{index > 0 && (
<hr
style={{
border: "0",
borderTop: "1px solid #ccc",
margin: "10px 0"
}}
/>
)}
<label className="data-label">Tipo de botella</label>
<p className="data-p">{p.forma_botella}</p>
<label className="data-label">Medida de la botella</label>
<p className="data-p">{p.medida_botella}</p>
<label className="data-label">Cantidad de botellas</label>
<p className="data-p">{p.cantidad_botellas}</p>
<label className="data-label">Tapón</label>
<p className="data-p">{p.tipo_corcho}</p>
</React.Fragment>
))
) : (
<p className="data-p">No hay partidas registradas</p>
)}
</div>
<div className="data-column">
<label className="data-label">Fecha de embotellado</label>
<p className="data-p">
{doc.embotellado.estiba_inicio
? new Date(doc.embotellado.estiba_inicio).toLocaleDateString('es-ES', {
day: '2-digit',
month: '2-digit',
year: 'numeric',
})
: '-'}
</p>
<label className="data-label">Fecha de salida a mercado</label>
<p className="data-p">
{doc.embotellado.estiba_final
? new Date(doc.embotellado.estiba_final).toLocaleDateString('es-ES', {
day: '2-digit',
month: '2-digit',
year: 'numeric',
})
: '-'}
</p>
{/* Vino comprado a granel no tiene concepto de "estiba" propia — con al
    menos una partida comprada, este campo queda siempre vacío. Mejor no
    mostrarlo en vez de un "-" sin explicación. */}
{!doc.partidas_usadas?.some(p => p.origen === 'stock') && (
<div className="gray-box">
<label className="data-label">Días totales de estiba</label>
<p className="data-p">
{doc.embotellado.dias_estiba ? `${doc.embotellado.dias_estiba}d` : '-'}
</p>
</div>
)}
<div className="gray-box">
<label className="data-label">Total de botellas</label>
<p className="data-p">
{doc.embotellado?.embotellados_detalle && doc.embotellado.embotellados_detalle.length > 0
? doc.embotellado.embotellados_detalle.reduce((total, p) => total + Number(p.cantidad_botellas), 0)
: 0
}
</p>
</div>
</div>
</div>
</>)}
</div>
);
case 'etiqueta':
return <EtiquetaTabContent doc={doc} mostrar={mostrar} refetchDoc={fetchDoc}/>;
default:
return null;
}
};
if (loading) return <LoadingSpinner/>;
if (!doc) return<p className="data-p">Documento no encontrado</p>;
const mostrar = (valor) => valor !== null && valor !== undefined && valor !== '' ? valor : '-';
const mostrarFecha = (valor) => {
if (!valor || valor.trim() === '') return '-'; 
const fecha = new Date(valor); 
if (isNaN(fecha)) return '-'; 
const dia = String(fecha.getDate()).padStart(2, '0');
const mes = String(fecha.getMonth() + 1).padStart(2, '0'); 
const anio = fecha.getFullYear();
return `${dia}/${mes}/${anio}`;
};
if (doc.coleccion === 'Viticultura') {
return (
<div className="doc-detail viticultura-detail" style={{ padding: '0 16px' }}>
<header className="trazabilidad-header">
<div className="header-content">
<h1>Trazabilidad</h1>
</div>
</header>
<div className="doc-detail header" style={{ display: 'flex', gap: '40px' }}>
<div className="header-column" style={{ flex: 1,flexDirection: 'row',gap: '10px', justifyContent: 'left', alignItems:'center' }}>
<div className="doc-icon-container">
<ViticulturaIcon className="doc-icon"/>
</div>
<p className="header-value" style={{fontSize: '18px', color: '#2c2c2c'}}>{mostrar(doc.id)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Creado por</label>
<p className="header-value">{mostrar(doc.creador)}</p>
<p className="header-value">{formatearFecha(doc.fecha_creacion)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Última modificación</label>
<p className="header-value">{mostrar(doc.modificador)}</p>
<p className="header-value">{formatearFecha(doc.fecha_modificacion)}</p>
</div>
</div>
<div className="columns-container" style={{ display: 'flex', gap: '40px' }}>
<div className="data-column">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{mostrar(doc.profesional_responsable)}</p>
<label className="data-label">Tipo de propiedad</label>
<p className="data-p" style={{textTransform: 'capitalize'}}>{mostrar(doc.pertenencia)}</p>
{doc.pertenencia?.toLowerCase() === 'propia' && (
<>
<label className="data-label">Área de cosecha</label>
<p className="data-p" style={{textTransform:'capitalize'}}>
{doc.provenienza?.toLowerCase() === 'hectarea' 
? 'Hectárea' 
: mostrar(doc.provenienza)}
</p>
</>
)}
{doc.pertenencia?.toLowerCase() === 'tercero' && (
<>
<label className="data-label">Nombre de la finca</label>
<p className="data-p">{mostrar(doc.nombre_finca)}</p>
</>
)}
<label className="data-label">Tipo de suelo</label>
<p className="data-p">{mostrar(doc.tipo_suelo)}</p>
<label className="data-label">Tipo de riego</label>
<p className="data-p">{mostrar(doc.tipo_riego)}</p>
<label className="data-label">Altura sobre el nivel del mar</label>
<p className="data-p">{doc.altura_snm ? `${doc.altura_snm}m` : '-'}</p>
<label className="data-label">Fecha de primera poda</label>
<p className="data-p">{mostrarFecha(doc.fecha_primera_poda)}</p>
<label className="data-label">Fecha de segunda poda</label>
<p className="data-p">{mostrarFecha(doc.fecha_segunda_poda)}</p>
</div>
<div className="data-column">
<label className="data-label">Fecha de cosecha</label>
<p className="data-p">{mostrarFecha(doc.fecha_cosecha)}</p>
<label className="data-label">Cepa</label>
<p className="data-p">{mostrar(doc.variedad_uva)}</p>
<label className="data-label">Nivel de acidéz</label>
<p className="data-p">{doc.nivel_acido ? `${doc.nivel_acido} g/L` : '-'}</p>
<label className="data-label">Nivel de azúcar</label>
<p className="data-p">{doc.nivel_azucar ? `${doc.nivel_azucar}° Brix` : '-'}</p>
<label className="data-label">Nivel de pH</label>
<p className="data-p">{mostrar(doc.nivel_ph)}</p>
<label className="data-label">Quintales</label>
<UsosTooltip
usos={doc.vinificaciones_que_lo_usan}
navigate={navigate}
formatPrefijo={(v) => v.quintales_consumidos ? `${v.quintales_consumidos} q en ` : 'en '}
>
<p className="data-p">
{doc.quintales
? `${doc.quintales_presentes ?? doc.quintales} / ${doc.quintales} q`
: '-'}
</p>
</UsosTooltip>
</div>
<div className="data-column">
<label className="data-label">Días de sol</label>
<p className="data-p">{doc.dias_sol ? `${doc.dias_sol} d` : '-'}</p>
<label className="data-label">Precipitación media anual</label>
<p className="data-p">{doc.precipitacion_media_anual ? `${doc.precipitacion_media_anual} mm` : '-'}</p>
<label className="data-label">Temperatura media anual</label>
<p className="data-p">{doc.temperatura_media_anual ? `${doc.temperatura_media_anual}° C` : '-'}</p>
<label className="data-label">Humedad media anual</label>
<p className="data-p">{doc.humedad_media_anual ? `${doc.humedad_media_anual}%` : '-'}</p>
<div className="button-container" style={{gap:'10px'}}>
<label className="data-label">Precio unitario</label>
<p className="data-p">{doc.precioUnidad ? `$ ${doc.precioUnidad.toLocaleString('es-AR')}` : '-'}</p>
<hr style={{ border: "0", borderTop: "1px solid #eaeef5", margin: "10px 0" }} />
<button className="button"
onClick={() => handleEdit('viticultura', doc.id)}
>Editar
</button>
</div>
</div>
</div>
</div>
);
}
else if (doc.coleccion === 'Embotellado') {
const totalDisponibleParaEtiquetar = (doc.embotellados_detalle || [])
.reduce((total, p) => total + (Number(p.disponibleParaEtiquetar) || 0), 0);
return (
<div className="doc-detail embotellado-detail" style={{ padding: '0 16px' }}>
<header className="trazabilidad-header">
<div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<h1>Trazabilidad</h1>
<UnidadVolumenToggle unidad={unidadVolumenDetalle} onChange={setUnidadVolumenDetalle} />
</div>
</header>
<div className="doc-detail header" style={{ display: 'flex', gap: '40px' }}>
<div className="header-column" style={{ flex: 1,flexDirection: 'row',gap: '10px', justifyContent: 'left', alignItems:'center' }}>
<div className="doc-icon-container">
<EmbotelladoIcon className="doc-icon"/>
</div>
<p className="header-value" style={{fontSize: '18px', color: '#2c2c2c'}}>{mostrar(doc.id)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Creado por</label>
<p className="header-value">{mostrar(doc.creador)}</p>
<p className="header-value">{formatearFecha(doc.fecha_creacion)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Última modificación</label>
<p className="header-value">{mostrar(doc.modificador)}</p>
<p className="header-value">{formatearFecha(doc.fecha_modificacion)}</p>
</div>
</div>
<div className="columns-container" style={{ display: 'flex', gap: '40px' }}>
<div className="data-column">
<label className="data-label">Profesional responsable</label>
<p className="data-p" style={{fontWeight: '700'}}>{mostrar(doc.profesional_responsable)}</p>
<label className="data-label">Tipo de vino</label>
<p className="data-p" style={{ textTransform: 'capitalize' }}>
{mostrar(doc.tipo_vino)}
</p>
<label className="data-label">Tipo de uso</label>
<p className="data-p" style={{ textTransform: 'capitalize' }}>
{mostrar(doc.tipo_uso)}
</p>
{doc.partidas_usadas && doc.partidas_usadas.length > 0 ? (
doc.partidas_usadas.map((p, index) => {
const idPartida = p.ref?.id || mostrar(p.ref);
return (
<> <hr style={{ border: "0", borderTop: "1px solid #ccc", margin: "0px 0 0px" }} />
<label className="data-label">Partida</label>
<p 
key={index} 
className="data-p link" 
style={{color: '#e65c5c'}} 
onClick={() => { navigate(`/trazabilidad/${idPartida}`); }}
>
{idPartida}
</p>
<label className="data-label">Cantidad de hectolitros usados</label>
<p className="data-p">{formatHl(p.cantidad_hl)}</p>
</>
);
})
) : (
<p className="data-p">No hay partidas registradas</p>
)}
</div>
<div className="data-column">
{doc.embotellados_detalle && doc.embotellados_detalle.length > 0 ? (
doc.embotellados_detalle.map((p, index) => {
return (
<React.Fragment key={index}>
{index > 0 && ( 
<hr style={{ border: "0", borderTop: "1px solid #ccc", margin: "10px 0" }} />
)}
<label className="data-label">Tipo de botella</label>
<p className="data-p">{p.forma_botella}</p>
<label className="data-label">Medida de la botella</label>
<p className="data-p">{p.medida_botella}</p>
<label className="data-label">Tapón</label>
<p className="data-p">{p.tipo_corcho}</p>
<label className="data-label">Cantidad de botellas</label>
{p.usos && p.usos.length > 0 ? (
<UsosTooltip
usos={p.usos}
navigate={navigate}
formatPrefijo={(u) => u.cantidad_botellas ? `${Number(u.cantidad_botellas).toLocaleString('es-AR')} en ` : 'en '}
>
<p className="data-p">
{Number(p.disponibleParaEtiquetar || 0).toLocaleString('es-AR')}/{Number(p.cantidad_botellas || 0).toLocaleString('es-AR')}
</p>
</UsosTooltip>
) : (
<p className="data-p">{Number(p.cantidad_botellas || 0).toLocaleString('es-AR')}</p>
)}
{p.disponibleParaEtiquetar > 0 && (
<>
<label className="data-label" style={{ marginTop: '20px' }}>Botellas a etiquetar</label>
<div className="input-group number">
<input
type="number"
min="0"
max={p.disponibleParaEtiquetar}
value={cantidadesEtiquetar[index] || ''}
onChange={(e) => setCantidadesEtiquetar(prev => ({ ...prev, [index]: e.target.value }))}
className="input"
placeholder="Ingresar cantidad"
/>
</div>
{parseInt(cantidadesEtiquetar[index], 10) > p.disponibleParaEtiquetar && (
<span style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra', margin: '0px 10px' }}>
Superaste el stock disponible.
</span>
)}
</>
)}
</React.Fragment>
);
})
) : (
<p className="data-p">No hay partidas registradas</p>
)}
{doc.vinos_creados && doc.vinos_creados.length > 0 && (
<>
<hr style={{ border: "0", borderTop: "1px solid #ccc", margin: "10px 0" }} />
<label className="data-label">Etiquetas</label>
{doc.vinos_creados.map((nombre, index) => (
<p key={index} className="data-p">{nombre}</p>
))}
</>
)}
</div>
<div className="data-column">
<label className="data-label">Fecha de embotellado</label>
<p className="data-p">
{doc.estiba_inicio
? new Date(doc.estiba_inicio).toLocaleDateString('es-ES', {
day: '2-digit',
month: '2-digit',
year: 'numeric',
})
: '-'}
</p>
<label className="data-label">Fecha de salida a mercado</label>
<p className="data-p">
{doc.estiba_final
? new Date(doc.estiba_final).toLocaleDateString('es-ES', {
day: '2-digit',
month: '2-digit',
year: 'numeric',
})
: '-'}
</p>
{/* Misma regla que en la vista de Trazabilidad: vino comprado a granel no
    tiene "estiba" propia, este campo queda siempre vacío en ese caso. */}
{!doc.partidas_usadas?.some(p => p.origen === 'stock') && (
<div className="gray-box">
<label className="data-label">Días totales de estiba</label>
<p className="data-p">
{doc.dias_estiba ? `${doc.dias_estiba}d` : '-'}
</p>
</div>
)}
<div className="gray-box">
<label className="data-label">Total de botellas</label>
<p className="data-p">
{doc.embotellados_detalle && doc.embotellados_detalle.length > 0
? doc.embotellados_detalle.reduce((total, p) => total + Number(p.cantidad_botellas), 0)
: 0
}
</p>
</div>
{etiquetarError && (
<p style={{ color: '#e65c5c', fontSize: '13px', fontFamily: 'Effra' }}>{etiquetarError}</p>
)}
<div className="button-container">
<hr style={{ border: "0", borderTop: "1px solid #eaeef5", margin: "0px 0 0px" }} />
<div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
<button className="button"
onClick={() => handleEdit('embotellado', doc.id)}
>Editar
</button>
{(() => {
const hayCantidadCargada = Object.values(cantidadesEtiquetar).some(v => parseInt(v, 10) > 0);
const haySobregiro = Object.entries(cantidadesEtiquetar).some(([index, valor]) => {
const disponible = (doc.embotellados_detalle?.[index])?.disponibleParaEtiquetar || 0;
return parseInt(valor, 10) > disponible;
});
const etiquetarInvalido = totalDisponibleParaEtiquetar <= 0 || !hayCantidadCargada || haySobregiro;
return (
<button className="button"
onClick={handleEtiquetar}
disabled={etiquetando || etiquetarInvalido}
style={etiquetarInvalido && !etiquetando ? { backgroundColor: '#f9fafd', color: '#b3b3b3', cursor: 'default' } : undefined}
>
<span className="button-text">{etiquetando ? 'Etiquetando...' : 'Etiquetar'}</span>
{etiquetando && (
<span className="progress-bar" style={{ width: `${etiquetarProgress}%` }}></span>
)}
</button>
);
})()}
</div>
</div>
</div>
</div>
</div>
);
}
else if (doc.coleccion === 'Vinificacion') {
return (
<div className="doc-detail embotellado-detail" style={{ padding: '0 16px' }}>
<header className="trazabilidad-header">
<div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<h1>Trazabilidad</h1>
<UnidadVolumenToggle unidad={unidadVolumenDetalle} onChange={setUnidadVolumenDetalle} />
</div>
</header>
<div className="doc-detail header" style={{ display: 'flex', gap: '40px' }}>
<div className="header-column" style={{ flex: 1,flexDirection: 'row',gap: '10px', justifyContent: 'left', alignItems:'center' }}>
<div className="doc-icon-container">
<VinificacionIcon className="doc-icon"/>
</div>
<p className="header-value" style={{fontSize: '18px', color: '#2c2c2c'}}>{mostrar(doc.id)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Creado por</label>
<p className="header-value">{mostrar(doc.creador)}</p>
<p className="header-value">{formatearFecha(doc.fecha_creacion)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Última modificación</label>
<p className="header-value">{mostrar(doc.modificador)}</p>
<p className="header-value">{formatearFecha(doc.fecha_modificacion)}</p>
</div>
</div>
<ul className="tabs-nav">
{vinTabs.map(tab => (
<li key={tab.id} className={activeVinTab === tab.id ? 'active' : ''}>
<a href="#" onClick={(e) => { e.preventDefault(); setActiveVinTab(tab.id); }}>
{tab.label}
</a>
</li>
))}
</ul>
{renderVinTabContent()}
</div>
);
}
if (doc.coleccion === 'Trazabilidad') {
return (
<div className="doc-detail viticultura-detail" style={{height:'100%',overflow:'hidden', padding:'0 16px'}}>
<header className="trazabilidad-header">
<div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
<h1>Trazabilidad</h1>
<UnidadVolumenToggle unidad={unidadVolumenDetalle} onChange={setUnidadVolumenDetalle} />
</div>
</header>
<div className="doc-detail header" style={{ display: 'flex', gap: '40px' }}>
<div className="header-column" style={{ flex: 1,flexDirection: 'row',gap: '10px', justifyContent: 'left', alignItems:'center' }}>
<div className="doc-icon-container">
<SearchIcon className="doc-icon"/>
</div>
<p className="header-value" style={{fontSize: '18px', color: '#2c2c2c'}}>{mostrar(doc.id)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Creado por</label>
<p className="header-value">{mostrar(doc.creador)}</p>
<p className="header-value">{formatearFecha(doc.fecha_creacion)}</p>
</div>
<div className="header-column" style={{ flex: 1 }}>
<label className="header-label">Última modificación</label>
<p className="header-value">{mostrar(doc.modificador)}</p>
<p className="header-value">{formatearFecha(doc.fecha_modificacion)}</p>
</div>
</div>
<ul className="tabs-nav">
{trazTabs.map(tab => (
<li key={tab.id} className={activeTrazTab === tab.id ? 'active' : ''}>
<a href="#" onClick={(e) => { e.preventDefault(); setActiveTrazTab(tab.id); }}>
{tab.label}
</a>
</li>
))}
</ul>
{renderTrazTabContent()}
</div>
);
}
return (
<div>
<h1>Detalle del documento {doc.id}</h1>
<p className="data-p">No hay un layout específico para esta colección.</p>
</div>
);
}
export default DocDetail;
